import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SchedulerPanel from '../components/SchedulerPanel';
import SchedulePlanModal from '../components/SchedulePlanModal';
import { cardTitle } from '../lib/cardTitle';
import { TalentAvailabilityView } from './TalentAvailabilityCalendar';

// ── Date helpers ──────────────────────────────────────────────────────────────
function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildCalDays(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  const days = [];
  const cur  = new Date(start);
  while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

function getWeekMonday(fromDate) {
  const d = new Date(fromDate);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
}

const MONTH_NAMES    = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES      = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const WEEK_DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
  for (const m of [0, 30]) {
    if (h === 23 && m === 30) break;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12  = h % 12 || 12;
    TIME_OPTIONS.push({ value: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`, label: `${h12}:${String(m).padStart(2,'0')} ${ampm}` });
  }
}

const STATUS_EVENT = {
  Scripted:   'ev-scripted',
  Filming:    'ev-filming',
  Filmed:     'ev-filmed',
  Editing:    'ev-editing',
  Qual:       'ev-qual',
  Approved:   'ev-approved',
  Incomplete: 'ev-incomplete',
  Posted:     'ev-posted',
};

const STATUS_PILL = {
  Scripted:   'pill-scripted',
  Filming:    'pill-filming',
  Filmed:     'pill-filmed',
  Editing:    'pill-editing',
  Qual:       'pill-qual',
  Approved:   'pill-approved',
  Incomplete: 'pill-incomplete',
  Posted:     'pill-posted',
};

const MAX_CHIPS_MONTH = 3;
const MAX_CHIPS_WEEK  = 5;

// Compact initials for a segment's talent list.
// - team_member: use stored initials when present
// - freeform display name: derive 2-letter initials from first 1–2 words
function segmentInitialsList(segment) {
  const out = [];
  for (const t of (segment?.talents || [])) {
    if (t.talent?.initials) { out.push(t.talent.initials); continue; }
    const name = t.talentDisplayName?.trim();
    if (!name) continue;
    const parts = name.split(/\s+/).filter(Boolean);
    const init = parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
    out.push(init);
  }
  return out;
}

const SCHEDULE_SLOTS = TIME_OPTIONS.filter(o => o.value >= '07:00' && o.value <= '21:00');

// Talent Calendar color language (owner call 2026-09-01): GOLDEN = available,
// GREEN = booked (explicit booking or on a shoot segment). Golden is
// deliberately soft — "not too bright".
const GOLD  = { text: 'rgba(230,200,110,0.95)', border: 'rgba(230,200,110,0.42)', bg: 'rgba(230,200,110,0.10)' };
const BOOKG = { text: '#4ade80', border: 'rgba(74,222,128,0.55)', bg: 'rgba(74,222,128,0.14)' };

// The peek popup and the day-schedule picker are gone (owner call
// 2026-09-01): scheduling lives in the Script List and the Talent Calendar's
// booking flow; the Shoot Schedule is a read-and-edit view — a chip click
// opens the Video Card directly.

// ── Month grid view ───────────────────────────────────────────────────────────
function CalendarGrid({ records, talentCounts = {}, onMoveSegment, onUnscheduleSegment, onOpenVideoCard, onLoadCounts, onDaySelect, onAssignTalent, onDayDetailOpen }) {
  const now = new Date();
  const [year,     setYear]     = useState(now.getFullYear());
  const [month,    setMonth]    = useState(now.getMonth());
  const [dragOver, setDragOver] = useState(null);
  // Busy days expand in place: "+N more ▾" shows every scheduled script as a
  // continuation inside the cell (owner call 2026-09-01); "Show less ▴" folds.
  const [expandedDays, setExpandedDays] = useState(() => new Set());

  const todayKey = dateKey(now);

  // Each chip is one segment-with-shoot-day. A project with 3 shoot days shows
  // up as 3 separate entries, one per day.
  const byDate = useMemo(() => {
    const map = {};
    for (const rec of records) {
      for (const segment of (rec.shootSegments || [])) {
        if (!segment.shootDay) continue;
        const k = segment.shootDay.slice(0, 10);
        if (!map[k]) map[k] = [];
        map[k].push({ rec, segment });
      }
    }
    // Stable sort within a day: by segment sortOrder so chip order matches Scheduler
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.segment.sortOrder ?? 0) - (b.segment.sortOrder ?? 0));
    }
    return map;
  }, [records]);

  const days = useMemo(() => buildCalDays(year, month), [year, month]);

  // Load talent counts whenever month changes
  useEffect(() => {
    if (!onLoadCounts) return;
    const from = `${year}-${String(month + 1).padStart(2,'0')}-01`;
    const last = new Date(year, month + 1, 0);
    const to   = `${year}-${String(month + 1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`;
    onLoadCounts(from, to);
  }, [year, month, onLoadCounts]);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else              setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else               setMonth(m => m + 1);
  }
  function goToday() { setYear(now.getFullYear()); setMonth(now.getMonth()); }

  function handleDayClick(key) {
    const events = byDate[key] || [];
    if (events.length > 0 && onDayDetailOpen) {
      onDayDetailOpen(key);
    }
    if (onDaySelect) onDaySelect(key);
  }

  return (
    <div className="cal-grid-wrap">
      <div className="cal-month-nav">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="cal-month-label">{MONTH_NAMES[month]} {year}</div>
          <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={goToday}>Today</button>
        </div>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>

      <div className="cal-grid">
        {DAY_NAMES.map(d => (
          <div key={d} className="cal-dow">{d}</div>
        ))}

        {days.map(day => {
          const key           = dateKey(day);
          const isToday       = key === todayKey;
          const otherMo       = day.getMonth() !== month;
          const events        = byDate[key] || [];
          const isExpanded    = expandedDays.has(key);
          const visible       = isExpanded ? events : events.slice(0, MAX_CHIPS_MONTH);
          const overflow      = events.length - MAX_CHIPS_MONTH;
          const talentCount   = talentCounts[key] || 0;

          return (
            <div
              key={key}
              className={`cal-day ${isToday ? 'today' : ''} ${otherMo ? 'other-month' : ''} ${dragOver === key ? 'drag-over' : ''}`}
              onClick={() => handleDayClick(key)}
              onDragOver={e => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault(); setDragOver(null);
                // Chip move (segment → new day) takes priority
                const segmentId = e.dataTransfer.getData('segment-id');
                if (segmentId) { onMoveSegment(segmentId, key); return; }
                const talentRaw = e.dataTransfer.getData('talent-chip');
                if (talentRaw && onAssignTalent) {
                  const chip      = JSON.parse(talentRaw);
                  const dayRecs   = byDate[key] || [];
                  if (dayRecs.length >= 1) onAssignTalent(dayRecs[0].rec.id, chip);
                }
              }}
            >
              {/* Clicking the top strip (around the number) selects the day so
                  the Talent pool below shows who is available — it never opens
                  the hourly modal. */}
              <div
                className="cal-day-head"
                title={otherMo ? undefined : 'Click: see talent available this day (Talent pool below)'}
                onClick={e => { e.stopPropagation(); if (!otherMo && onDaySelect) onDaySelect(key); }}
                style={otherMo ? undefined : { cursor: 'pointer' }}
              >
                <div className="cal-day-num-wrap">
                  <span className="cal-day-num">{day.getDate()}</span>
                  {talentCount > 0 && (
                    <span
                      title={`${talentCount} talent available`}
                      style={{
                        marginLeft: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 16, height: 16, borderRadius: '50%', background: 'rgba(139,109,255,0.25)',
                        color: 'var(--violet)', fontSize: 9, fontWeight: 700, verticalAlign: 'middle',
                      }}
                    >{talentCount}</span>
                  )}
                </div>
                <div className="cal-day-actions">
                  {events.length > 0 && (
                    <button
                      type="button"
                      className="cal-day-icon"
                      title="Hourly schedule for this day"
                      onClick={e => { e.stopPropagation(); if (onDayDetailOpen) onDayDetailOpen(key); if (onDaySelect) onDaySelect(key); }}
                    >🕐</button>
                  )}
                </div>
              </div>
              <div className="cal-events">
                {visible.map(({ rec, segment }) => {
                  const title     = cardTitle(rec);
                  const initsList = segmentInitialsList(segment);
                  const initsStr  = initsList.join(', ');
                  const talNames  = (segment.talents || [])
                    .map(t => t.talent?.name || t.talentDisplayName || '')
                    .filter(Boolean)
                    .join(', ');
                  const evCls     = STATUS_EVENT[rec.status] || 'ev-scripted';
                  // Run 5 (D-060): a booked talent whose availability does not
                  // cover this day renders the chip RED — state-based, overrides
                  // included. Red says what is; the alert email says what changed.
                  const uncovered = (segment.talents || []).filter(t => t.covered === false);
                  const redReason = uncovered.length
                    ? uncovered.map(t => `⚠ ${coverageReason(t.talent?.name || t.talentDisplayName || 'Talent', 'this day', t.coverage)}`).join('\n')
                    : '';
                  const segFilmed = segment.shootStatus === 'filmed';
                  return (
                    <div
                      key={segment.id}
                      className={`cal-event cal-event-multi ${evCls}`}
                      title={`${segment.segmentLabel} — ${title}${talNames ? ` (${talNames})` : ''}${segFilmed ? '\n✓ Filmed' : ''}${redReason ? `\n${redReason}` : ''}`}
                      draggable={!segFilmed}
                      style={{ cursor: 'pointer', position: 'relative',
                               ...(segFilmed ? { opacity: 0.45 } : {}),
                               ...(redReason && !segFilmed ? { background: 'rgba(212,85,85,0.18)', border: '1px solid rgba(212,85,85,0.65)' } : {}) }}
                      onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('segment-id', segment.id); }}
                      onDragEnd={() => {}}
                      onClick={e => { e.stopPropagation(); onOpenVideoCard(rec); }}
                    >
                      <div className="cal-ev-segment">{segFilmed ? '✓ ' : ''}{segment.segmentLabel}</div>
                      <div className="cal-ev-title">{title}</div>
                      {initsStr && <div className="cal-ev-talent">{initsStr}</div>}
                      <button
                        className="cal-ev-remove"
                        title="Remove from this date"
                        onClick={e => { e.stopPropagation(); onUnscheduleSegment(segment.id); }}
                      >×</button>
                    </div>
                  );
                })}
                {!isExpanded && overflow > 0 && (
                  <div
                    className="cal-overflow"
                    role="button"
                    tabIndex={0}
                    title={`Show all ${events.length} scheduled scripts in this day`}
                    style={{ cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); setExpandedDays(prev => new Set(prev).add(key)); }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setExpandedDays(prev => new Set(prev).add(key)); } }}
                  >+{overflow} more ▾</div>
                )}
                {isExpanded && events.length > MAX_CHIPS_MONTH && (
                  <div
                    className="cal-overflow"
                    role="button"
                    tabIndex={0}
                    title="Collapse back to the first few"
                    style={{ cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); setExpandedDays(prev => { const n = new Set(prev); n.delete(key); return n; }); }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setExpandedDays(prev => { const n = new Set(prev); n.delete(key); return n; }); } }}
                  >Show less ▴</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}

// ── Week grid view ────────────────────────────────────────────────────────────
function WeekGrid({ records, onMoveSegment, onUnscheduleSegment, onOpenVideoCard }) {
  const now = new Date();
  const [baseDate,  setBaseDate]  = useState(() => getWeekMonday(now));
  const [dragOver,  setDragOver]  = useState(null);
  const [expandedDays, setExpandedDays] = useState(() => new Set());

  const todayKey = dateKey(now);

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(baseDate);
      d.setDate(baseDate.getDate() + i);
      days.push(d);
    }
    return days;
  }, [baseDate]);

  const byDate = useMemo(() => {
    const map = {};
    for (const rec of records) {
      for (const segment of (rec.shootSegments || [])) {
        if (!segment.shootDay) continue;
        const k = segment.shootDay.slice(0, 10);
        if (!map[k]) map[k] = [];
        map[k].push({ rec, segment });
      }
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.segment.sortOrder ?? 0) - (b.segment.sortOrder ?? 0));
    }
    return map;
  }, [records]);

  function prevWeek() { const d = new Date(baseDate); d.setDate(d.getDate() - 7); setBaseDate(d); }
  function nextWeek() { const d = new Date(baseDate); d.setDate(d.getDate() + 7); setBaseDate(d); }
  function goToday()  { setBaseDate(getWeekMonday(now)); }

  const weekEnd   = weekDays[6];
  const weekStart = weekDays[0];
  const weekLabel = weekStart.getMonth() === weekEnd.getMonth()
    ? `${MONTH_NAMES[weekStart.getMonth()].slice(0, 3)} ${weekStart.getDate()}–${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
    : `${MONTH_NAMES[weekStart.getMonth()].slice(0, 3)} ${weekStart.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()].slice(0, 3)} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;

  return (
    <div className="cal-grid-wrap">
      <div className="cal-month-nav">
        <button className="cal-nav-btn" onClick={prevWeek}>‹</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="cal-month-label">{weekLabel}</div>
          <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={goToday}>Today</button>
        </div>
        <button className="cal-nav-btn" onClick={nextWeek}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {/* Day headers */}
        {weekDays.map((day, i) => {
          const key     = dateKey(day);
          const isToday = key === todayKey;
          return (
            <div
              key={`wh-${key}`}
              style={{
                padding: '10px 8px 8px',
                borderRight: i < 6 ? '1px solid var(--border)' : 'none',
                borderBottom: '1px solid var(--border)',
                background: isToday ? 'var(--violet-soft)' : 'var(--bg-card)',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 10, color: 'var(--tx-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {WEEK_DAY_NAMES[i]}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: isToday ? 'var(--violet)' : 'var(--tx-primary)', lineHeight: 1.2, marginTop: 2 }}>
                {day.getDate()}
              </div>
            </div>
          );
        })}

        {/* Day cells */}
        {weekDays.map((day, i) => {
          const key     = dateKey(day);
          const isToday = key === todayKey;
          const events  = byDate[key] || [];
          const isExpanded = expandedDays.has(key);
          const visible = isExpanded ? events : events.slice(0, MAX_CHIPS_WEEK);
          const overflow = events.length - MAX_CHIPS_WEEK;

          return (
            <div
              key={`wc-${key}`}
              style={{
                minHeight: 160, padding: '6px 4px',
                borderRight: i < 6 ? '1px solid var(--border)' : 'none',
                background: isToday
                  ? 'rgba(139,109,255,0.04)'
                  : dragOver === key ? 'var(--bg-hover)' : 'transparent',
              }}
              onDragOver={e => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault(); setDragOver(null);
                const segmentId = e.dataTransfer.getData('segment-id');
                if (segmentId) onMoveSegment(segmentId, key);
              }}
            >
              {visible.map(({ rec, segment }) => {
                const title     = cardTitle(rec);
                const initsList = segmentInitialsList(segment);
                const initsStr  = initsList.join(', ');
                const talNames  = (segment.talents || [])
                  .map(t => t.talent?.name || t.talentDisplayName || '')
                  .filter(Boolean)
                  .join(', ');
                const evCls     = STATUS_EVENT[rec.status] || 'ev-scripted';
                const uncovered = (segment.talents || []).filter(t => t.covered === false);
                const redReason = uncovered.length
                  ? `⚠ ${uncovered.map(t => t.talent?.name || t.talentDisplayName).filter(Boolean).join(', ')} not available this day`
                  : '';
                return (
                  <div
                    key={segment.id}
                    className={`cal-event cal-event-multi ${evCls}`}
                    title={`${segment.segmentLabel} — ${title}${talNames ? ` (${talNames})` : ''}${redReason ? `\n${redReason}` : ''}`}
                    draggable
                    style={{ cursor: 'pointer', marginBottom: 3, position: 'relative',
                             ...(redReason ? { background: 'rgba(212,85,85,0.18)', border: '1px solid rgba(212,85,85,0.65)' } : {}) }}
                    onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('segment-id', segment.id); }}
                    onDragEnd={() => {}}
                    onClick={e => { e.stopPropagation(); onOpenVideoCard(rec); }}
                  >
                    <div className="cal-ev-segment">{segment.segmentLabel}</div>
                    <div className="cal-ev-title">{title}</div>
                    {initsStr && <div className="cal-ev-talent">{initsStr}</div>}
                    <button
                      className="cal-ev-remove"
                      title="Remove"
                      onClick={e => { e.stopPropagation(); onUnscheduleSegment(segment.id); }}
                    >×</button>
                  </div>
                );
              })}
              {!isExpanded && overflow > 0 && (
                <div className="cal-overflow" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                     title={`Show all ${events.length} scheduled scripts in this day`}
                     onClick={() => setExpandedDays(prev => new Set(prev).add(key))}>
                  +{overflow} more ▾
                </div>
              )}
              {isExpanded && events.length > MAX_CHIPS_WEEK && (
                <div className="cal-overflow" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                     title="Collapse back to the first few"
                     onClick={() => setExpandedDays(prev => { const n = new Set(prev); n.delete(key); return n; })}>
                  Show less ▴
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

// ── Roles that can see talent availability ─────────────────────────────────────
function canSeeTalentAvailability(user) {
  const r = user?.roles || [];
  return r.some(x => ['Admin','Power User','Producer','Writer','Qual Reviewer','Qual_Reviewer'].includes(x));
}
function canAssignTalent(user) {
  const r = user?.roles || [];
  return r.some(x => ['Admin','Power User','Producer'].includes(x));
}

function canReorderShoots(user) {
  const r = user?.roles || [];
  return r.some(x => ['Admin','Power User','Producer','Power_User'].includes(x));
}

// Human message for a broken booking — distinguishes "wrong TIME" (available
// that day, scheduled outside their window) from "wrong DAY" (2026-09-03,
// owner call: the day-level message misled when only the time was off).
function coverageReason(name, dayLabel, cov) {
  if (cov?.dayAvailType && cov.shootTimeSlot) {
    const win = cov.dayAvailType === 'full_day'
      ? 'all day'
      : `${fmtTime12(cov.dayBlockStart)}–${fmtTime12(cov.dayBlockEnd)}`;
    return `${name} is available ${dayLabel} but not at ${fmtTime12(cov.shootTimeSlot)} — their window is ${win}`;
  }
  return `${name} is not available ${dayLabel}`;
}

function fmtSlot(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
}

function fmtTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12  = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
}

// ── Mini date picker (for StaffAvailabilityModal "Repeat until" field) ────────
function MiniDatePicker({ value, onChange, placeholder = 'Select date' }) {
  const [open,      setOpen]      = useState(false);
  const [dropPos,   setDropPos]   = useState({ top: 0, left: 0 });
  const [viewYear,  setViewYear]  = useState(() => { const d = value ? new Date(value + 'T00:00:00') : new Date(); return d.getFullYear(); });
  const [viewMonth, setViewMonth] = useState(() => { const d = value ? new Date(value + 'T00:00:00') : new Date(); return d.getMonth(); });
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (btnRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  }
  function prevM() { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); }
  function nextM() { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); }

  const days        = buildCalDays(viewYear, viewMonth);
  const todayKey_   = dateKey(new Date());
  const selectedKey = value || '';
  const displayLabel = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : placeholder;

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} className="form-input"
        style={{ textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: value ? 'var(--tx-primary)' : 'var(--tx-faint)' }}>
        {displayLabel}
        <span style={{ color: 'var(--tx-muted)', fontSize: 11, marginLeft: 8 }}>▾</span>
      </button>
      {open && (
        <div ref={dropRef} style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 10000, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', width: 230, boxShadow: '0 6px 28px rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" className="cal-nav-btn" onClick={prevM}>‹</button>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)' }}>{MONTH_NAMES[viewMonth]} {viewYear}</div>
            <button type="button" className="cal-nav-btn" onClick={nextM}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {['S','M','T','W','T','F','S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 9, color: 'var(--tx-faint)', fontWeight: 600, padding: '3px 0' }}>{d}</div>
            ))}
            {days.map(day => {
              const k = dateKey(day);
              const isToday = k === todayKey_;
              const isSel   = k === selectedKey;
              const other   = day.getMonth() !== viewMonth;
              return (
                <button key={k} type="button"
                  onClick={() => { if (!other) { onChange(k); setOpen(false); } }}
                  style={{ padding: '5px 0', fontSize: 11, borderRadius: 4, border: 'none', fontFamily: 'var(--font)', cursor: other ? 'default' : 'pointer',
                    background: isSel ? 'var(--violet)' : isToday ? 'rgba(139,109,255,0.2)' : 'transparent',
                    color: isSel ? '#fff' : isToday ? 'var(--violet)' : other ? 'var(--tx-faint)' : 'var(--tx-primary)',
                    fontWeight: isSel || isToday ? 600 : 400 }}>
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          {value && (
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              style={{ marginTop: 8, fontSize: 11, color: 'var(--tx-muted)', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'center', padding: '3px 0', fontFamily: 'var(--font)' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </>
  );
}

// ── Talent shoots-on-day modal ────────────────────────────────────────────────
// Click a name chip in the Talent Calendar → shows the video segments that
// talent is scheduled to shoot on that day. Pure read-only.
function TalentShootsModal({ talent, dateStr, segments, loading, onClose }) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const prettyDate = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="vc-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">Scheduled Shoots</div>
            <div className="vc-title">{talent.name}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tx-muted)' }}>{prettyDate}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '20px 28px 28px' }}>
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)', padding: '12px 0' }}>Loading…</div>
          ) : segments.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--tx-muted)', padding: '24px 0', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)' }}>
              No shoots scheduled for {talent.name} on this day.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {segments.map(seg => {
                const lp = seg.liveProduction;
                const title = lp?.script?.scriptName || '(Untitled)';
                return (
                  <div
                    key={seg.id}
                    style={{
                      padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-hover)', border: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {title}
                      </div>
                      <span className={`pill ${STATUS_PILL[lp?.status] || 'pill-scripted'}`} style={{ flexShrink: 0 }}>
                        {lp?.status || '—'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
                      {seg.segmentLabel}
                      {lp?.client?.name && ` · ${lp.client.name}`}
                      {seg.setting && ` · ${seg.setting}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Day matches modal (Talent Calendar double-click) ─────────────────────────
// Double-click a day in browse mode → every Approved, still-unscheduled script
// whose cast overlaps that day's BOOKED talent (bookings + talent on shoot
// segments), ordered by overlap: a script whose whole cast is booked tops the
// list. Clicking a row hands off to the Schedule Plan modal with this day
// pre-filled.
// ── Schedule mini-modal: "All Day" or a specific time (2026-09-02 overhaul) ──
// Mirrors the talent-availability all-day/timed pattern: the producer either
// says "show up whenever you're available that day" or pins a start time.
function ScheduleShootModal({ row, dateStr, onDone, onClose }) {
  const [allDay, setAllDay] = useState(true);
  const [time,   setTime]   = useState('12:00');
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');
  const [avail,  setAvail]  = useState(null); // that day's availability rows

  const [y, m, d] = dateStr.split('-').map(Number);
  const pretty = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  // Availability windows for the day, so a picked time outside a talent's
  // window warns BEFORE committing (2026-09-03, owner call).
  useEffect(() => {
    api.get(`/api/talent-availability/by-date?date=${dateStr}`)
      .then(r => setAvail(r.data || []))
      .catch(() => setAvail([]));
  }, [dateStr]);

  const timeWarnings = useMemo(() => {
    if (allDay || !time || !avail) return [];
    const t = time.slice(0, 5);
    const seenIds = new Set();
    const warnings = [];
    for (const seg of row.segments) {
      if (!seg.shootable) continue;
      for (const tal of seg.talents) {
        if (!tal.talentId || seenIds.has(tal.talentId)) continue;
        seenIds.add(tal.talentId);
        const av = avail.find(a => a.talentId === tal.talentId);
        if (!av) continue; // booked without an availability row — nothing to check against
        if (av.availabilityType === 'full_day') continue;
        const start = (av.blockStart || '').slice(0, 5);
        const end   = (av.blockEnd   || '').slice(0, 5);
        if (start && end && (t < start || t >= end)) {
          warnings.push(`${tal.name}'s window is ${fmtTime12(start)}–${fmtTime12(end)}`);
        }
      }
    }
    return warnings;
  }, [allDay, time, avail, row.segments]);

  async function handleSchedule() {
    setBusy(true);
    setErr('');
    try {
      // One PATCH per shootable segment — same endpoint the Scheduler panel
      // uses, so status sync and the shoot_scheduled_date cache stay correct.
      for (const segId of row.shootableSegmentIds) {
        await api.patch(`/api/shoot-segments/${segId}`, {
          shootDay:      dateStr,
          shootTimeSlot: allDay ? null : time,
        });
      }
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Scheduling failed.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1200 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 380 }}>
        <div className="modal-header">
          <div className="modal-title">Schedule — {row.title}</div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 12 }}>
            {pretty} · {row.availableCount === row.totalUnfilmed
              ? `all ${row.totalUnfilmed === 1 ? 'segment' : `${row.totalUnfilmed} segments`}`
              : `${row.availableCount} of ${row.totalUnfilmed} segments`} will be scheduled
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer', marginBottom: 8 }}>
            <input type="radio" checked={allDay} onChange={() => setAllDay(true)} />
            All Day <span style={{ fontSize: 11, color: 'var(--tx-faint)' }}>— shoot whenever the talent is in</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer' }}>
            <input type="radio" checked={!allDay} onChange={() => setAllDay(false)} />
            At a time
            {!allDay && (
              <input
                type="time"
                className="form-input"
                style={{ fontSize: 12, padding: '4px 8px', width: 110 }}
                value={time}
                onChange={e => setTime(e.target.value)}
              />
            )}
          </label>
          {timeWarnings.length > 0 && (
            <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.08)', border: '1px solid rgba(212,85,85,0.3)', fontSize: 12, color: 'var(--s-incomplete-t)' }}>
              {timeWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 4 }}>
                You can still schedule — the tile will show red until the time or their availability changes.
              </div>
            </div>
          )}
          {err && <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)', marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className={timeWarnings.length ? 'btn-warn' : 'btn-primary'}
            style={timeWarnings.length ? { background: 'rgba(255,200,80,0.9)', color: '#1a1a1a' } : undefined}
            onClick={handleSchedule}
            disabled={busy || (!allDay && !time)}
          >
            {busy ? 'Scheduling…' : timeWarnings.length ? 'Schedule anyway' : 'Schedule ✓'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DayMatchesModal({ dateStr, canSchedule, onScheduled, onClose }) {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState('');
  const [scheduling, setScheduling] = useState(null); // row | null
  const [tick,       setTick]       = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/api/tal/day-matches?date=${dateStr}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.error || 'Failed to load scripts for this day.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateStr, tick]);

  const [y, m, d] = dateStr.split('-').map(Number);
  const prettyDate = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const booked     = data?.booked || [];
  const available  = data?.available || [];
  const rows       = data?.rows || [];

  // Per-segment talent chip: green = booked this day, red-ish = assigned but
  // NOT booked (blocks the segment), grey italic = unassigned open slot.
  const talentChip = (t, i) => (
    <span
      key={t.talentId || `n${i}`}
      title={t.talentId
        ? (t.booked ? `${t.name} is booked this day` : `${t.name} is assigned but NOT booked this day`)
        : 'No talent assigned to this slot — assign talent before it can be scheduled'}
      style={{
        fontSize: 10, fontWeight: t.talentId ? 700 : 400, padding: '2px 7px', borderRadius: 'var(--radius-sm)',
        fontStyle: t.talentId ? 'normal' : 'italic',
        background: !t.talentId ? 'var(--bg-elevated)' : t.booked ? 'rgba(74,222,128,0.12)' : 'rgba(212,85,85,0.10)',
        border: !t.talentId ? '1px dashed var(--border)' : t.booked ? '1px solid rgba(74,222,128,0.5)' : '1px solid rgba(212,85,85,0.4)',
        color: !t.talentId ? 'var(--tx-faint)' : t.booked ? '#4ade80' : 'var(--s-incomplete-t)',
      }}
    >
      {t.name || 'unassigned'}
    </span>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 660, display: 'flex', flexDirection: 'column' }}>
        <div className="vc-header">
          <div style={{ minWidth: 0 }}>
            <div className="vc-label">Shootable Scripts</div>
            <div className="vc-title">{prettyDate}</div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {loading ? 'Loading…'
                : booked.length === 0 ? 'No talent booked this day.'
                : <>Booked: {booked.map(t => (
                    <span key={t.id} style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-sm)',
                      background: BOOKG.bg, border: `1px solid ${BOOKG.border}`, color: BOOKG.text,
                    }}>{t.name}</span>
                  ))}</>}
            </div>
            {!loading && available.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                Available: {available.map(t => (
                  <span key={t.id} style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-sm)',
                    background: GOLD.bg, border: `1px solid ${GOLD.border}`, color: GOLD.text,
                  }}>{t.name}</span>
                ))}
              </div>
            )}
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '16px 28px 24px', overflowY: 'auto', maxHeight: '58vh' }}>
          {err && <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)', marginBottom: 10 }}>{err}</div>}
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)', padding: '12px 0' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--tx-muted)', padding: '24px 0', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)' }}>
              {booked.length === 0
                ? 'Nobody is booked this day yet — click a golden (available) name on the calendar to book talent, then double-click the day again.'
                : 'No assigned scripts have segments this day’s booked talent can shoot.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map(row => {
                const allAvail = row.availableCount === row.totalUnfilmed;
                return (
                  <div
                    key={row.liveProductionId}
                    data-lpid={row.liveProductionId}
                    style={{
                      padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-hover)', border: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 }}>
                      {row.title}
                      {row.isRiff && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--tx-muted)', fontWeight: 400 }}>(Riff)</span>}
                      {row.isSeries && <span className="format-badge lf" style={{ marginLeft: 6, fontSize: 9 }}>LF</span>}
                      {row.isSeries && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--tx-muted)', fontWeight: 400 }} data-testid="dm-series-episodes">{row.episodeCount} episode{row.episodeCount === 1 ? '' : 's'}</span>}
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--tx-muted)', fontWeight: 400 }}>{row.client?.name}</span>
                    </div>
                    {/* One line per unfilmed segment: label + its talent chips. */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                      {row.segments.map(seg => (
                        <div key={seg.id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', opacity: seg.shootable ? 1 : 0.55 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--violet)', flexShrink: 0 }}>
                            {seg.episodeNumber ? `Ep ${seg.episodeNumber} · ${seg.segmentLabel}` : seg.segmentLabel}
                          </span>
                          {seg.talents.length === 0
                            ? talentChip({ talentId: null, name: null }, 0)
                            : seg.talents.map((t, i) => talentChip(t, i))}
                          {seg.shootDay && (
                            <span style={{ fontSize: 10, color: 'var(--tx-faint)' }}>
                              · set for {seg.shootDay}{seg.shootTimeSlot ? ` ${fmtTime12(seg.shootTimeSlot)}` : ''}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* Footer: Schedule button center, availability count bottom-right. */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
                      <span />
                      {canSchedule ? (
                        <button
                          className="btn-primary"
                          style={{ fontSize: 11, padding: '4px 16px' }}
                          onClick={() => setScheduling(row)}
                          title="Set the shoot day (all day or a time) for the segments shootable today"
                        >
                          Schedule
                        </button>
                      ) : <span />}
                      <span style={{
                        justifySelf: 'end', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-sm)',
                        background: allAvail ? 'rgba(74,222,128,0.15)' : 'rgba(255,200,80,0.12)',
                        color: allAvail ? '#4ade80' : 'rgba(255,200,80,0.95)',
                      }}>
                        {allAvail ? 'All segments available' : `${row.availableCount} of ${row.totalUnfilmed} segments available`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {scheduling && (
        <ScheduleShootModal
          row={scheduling}
          dateStr={dateStr}
          onClose={() => setScheduling(null)}
          onDone={() => {
            setScheduling(null);
            setTick(t => t + 1);   // re-pull the day's matches
            if (onScheduled) onScheduled(); // refresh calendar chips behind
          }}
        />
      )}
    </div>
  );
}

// ── Staff availability modal (same as talent's but posts on behalf of a talent) ─
function StaffAvailabilityModal({ dateStr, existing, forTalentId, onClose, onSaved, onDeleted }) {
  const isEdit = Boolean(existing);
  const [avType,     setAvType]     = useState(() => { const t = existing?.availabilityType || 'full_day'; return t === 'block' ? 'custom' : t; });
  const [blockStart, setBlockStart] = useState(existing?.blockStart || '09:00');
  const [blockEnd,   setBlockEnd]   = useState(() => {
    if (existing?.blockEnd) return existing.blockEnd;
    if (existing?.blockStart) {
      const [h, min] = existing.blockStart.split(':').map(Number);
      return `${String(h + 2).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
    }
    return '11:00';
  });
  const [isRecurring, setIsRecurring] = useState(existing?.isRecurring || false);
  const [recurUntil,  setRecurUntil]  = useState(existing?.recurrenceEndDate ? existing.recurrenceEndDate.slice(0,10) : '');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const [delMode, setDelMode] = useState(null);

  const [y, m, d] = dateStr.split('-').map(Number);
  const prettyDate = new Date(y, m-1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  async function handleSave() {
    setSaving(true); setErr('');
    try {
      const hasBlock = avType === 'block' || avType === 'custom';
      const payload = {
        date: dateStr,
        availabilityType: avType,
        blockStart:  hasBlock ? blockStart : undefined,
        blockEnd:    avType === 'custom'  ? blockEnd  : undefined,
        isRecurring,
        recurrenceEndDate: isRecurring && recurUntil ? recurUntil : undefined,
      };
      if (isEdit) {
        await api.patch(`/api/talent-availability/${existing.id}`, payload);
      } else {
        await api.post('/api/talent-availability', { ...payload, talentId: forTalentId });
      }
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  }

  async function handleDelete(mode) {
    setSaving(true); setErr('');
    try {
      const params = mode === 'occurrence' ? `?mode=occurrence&date=${dateStr}`
                   : mode === 'future'     ? `?mode=future&date=${dateStr}` : '';
      await api.delete(`/api/talent-availability/${existing.id}${params}`);
      onDeleted();
    } catch (e) { setErr(e.response?.data?.error || 'Delete failed.'); }
    finally { setSaving(false); setDelMode(null); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Availability' : 'Mark Availability'}</div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 16 }}>{prettyDate}</div>
          {err && <div className="form-error" style={{ marginBottom: 12 }}>{err}</div>}

          {delMode && (
            <div style={{ padding: '14px', background: 'rgba(42,14,14,0.5)', border: '1px solid rgba(212,85,85,0.2)', borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--tx-primary)', marginBottom: 12 }}>
                {delMode === 'occurrence' ? `Remove just ${prettyDate}?` : `Remove ${prettyDate} and all future occurrences?`}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setDelMode(null)}>Cancel</button>
                <button style={{ padding: '7px 16px', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  onClick={() => handleDelete(delMode)} disabled={saving}>
                  {saving ? 'Removing…' : 'Confirm Remove'}
                </button>
              </div>
            </div>
          )}

          <label className="form-label">Availability type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['full_day', 'custom'].map(v => (
              <button key={v} onClick={() => setAvType(v)} style={{ padding: '7px 18px', fontSize: 12, borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontFamily: 'var(--font)', fontWeight: avType === v ? 600 : 400, background: avType === v ? 'var(--violet)' : 'var(--bg-card)', color: avType === v ? '#fff' : 'var(--tx-secondary)', border: avType === v ? '1px solid var(--violet)' : '1px solid var(--border)' }}>
                {v === 'full_day' ? 'All day' : 'Custom times'}
              </button>
            ))}
          </div>

          {avType === 'custom' && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Start time</label>
                  <select className="form-input" value={blockStart} onChange={e => setBlockStart(e.target.value)}>
                    {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">End time</label>
                  <select className="form-input" value={blockEnd} onChange={e => setBlockEnd(e.target.value)}>
                    {TIME_OPTIONS.filter(o => o.value > blockStart).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              {blockEnd <= blockStart && <div style={{ fontSize: 11, color: 'var(--s-incomplete-t)', marginTop: 6 }}>End time must be after start time.</div>}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isRecurring ? 12 : 0 }}>
            <input type="checkbox" id="recur-staff" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} style={{ width: 16, height: 16 }} />
            <label htmlFor="recur-staff" style={{ fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer' }}>Repeat weekly</label>
          </div>
          {isRecurring && (
            <div style={{ marginTop: 8 }}>
              <label className="form-label">Repeat until (optional)</label>
              <MiniDatePicker value={recurUntil} onChange={setRecurUntil} placeholder="Repeat indefinitely" />
              <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 4 }}>Leave blank to repeat indefinitely.</div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {isEdit && (
              <div style={{ display: 'flex', gap: 8 }}>
                {existing.isRecurring ? (
                  <>
                    <button style={{ padding: '7px 12px', background: 'none', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)' }} onClick={() => setDelMode('occurrence')}>Remove this date</button>
                    <button style={{ padding: '7px 12px', background: 'none', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)' }} onClick={() => setDelMode('future')}>Remove all future</button>
                  </>
                ) : (
                  <button style={{ padding: '7px 14px', background: 'none', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }} onClick={() => handleDelete('all')} disabled={saving}>Remove</button>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || (avType === 'custom' && blockEnd <= blockStart)}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Day Detail Modal (opens from month/week cell click when cards exist) ─────────
// Multi-day shoots: this modal operates on SEGMENTS, not projects. Each row in
// the hour grid is one Cam, with its own shoot_time_slot. Two Cams of the same
// project on different days can have independent time slots.
//
// Props:
//   entries: [{ segment, rec }] — segments that have shoot_day === dateStr.
//   onSegmentTimeSlot(segmentId, timeSlot | null)
//   onSegmentReorder(dateStr, [{ id, sortOrder }])
function DayDetailModal({ dateStr, entries, talentForDay, canReorder, onClose, onSegmentTimeSlot, onSegmentReorder, onOpenVideoCard }) {
  // Local copies so optimistic updates feel snappy. Re-sync when entries prop changes.
  const [localEntries, setLocalEntries] = useState(() => entries.map(e => ({ ...e, segment: { ...e.segment } })));
  const [dragOverSlot, setDragOverSlot] = useState(null);

  useEffect(() => {
    setLocalEntries(entries.map(e => ({ ...e, segment: { ...e.segment } })));
  }, [entries]);

  const [y, mo, d] = dateStr.split('-').map(Number);
  const prettyDate = new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const sorted = [...localEntries].sort((a, b) => {
    const ao = a.segment.sortOrder ?? 0;
    const bo = b.segment.sortOrder ?? 0;
    if (ao !== bo) return ao - bo;
    const at = a.segment.shootTimeSlot;
    const bt = b.segment.shootTimeSlot;
    if (at && bt) return at.localeCompare(bt);
    if (at) return -1;
    if (bt) return 1;
    return 0;
  });
  const placed       = sorted.filter(e => e.segment.shootTimeSlot);
  const unscheduledE = sorted.filter(e => !e.segment.shootTimeSlot);

  const slotMap = {};
  for (const e of placed) {
    const k = e.segment.shootTimeSlot;
    if (!slotMap[k]) slotMap[k] = [];
    slotMap[k].push(e);
  }

  const talentAvailMap = {};
  for (const av of (talentForDay || [])) talentAvailMap[av.talentId] = av;

  // Conflict check: any segment talent unavailable in the chosen time slot.
  function getTalentConflict(entry) {
    const slot = entry.segment.shootTimeSlot;
    const talents = entry.segment.talents || [];
    if (talents.length === 0) return false;
    for (const t of talents) {
      if (!t.talentId) continue;
      const av = talentAvailMap[t.talentId];
      if (!av) return true;
      if (av.availabilityType === 'full_day') continue;
      if (slot && av.blockStart && av.blockEnd) {
        if (slot < av.blockStart || slot >= av.blockEnd) return true;
      }
    }
    return false;
  }

  async function handleDrop(timeSlot, segmentId) {
    const prevSlot = localEntries.find(e => e.segment.id === segmentId)?.segment.shootTimeSlot ?? null;
    setLocalEntries(p => p.map(e => e.segment.id === segmentId
      ? { ...e, segment: { ...e.segment, shootTimeSlot: timeSlot } } : e));
    try { await onSegmentTimeSlot(segmentId, timeSlot); }
    catch {
      setLocalEntries(p => p.map(e => e.segment.id === segmentId
        ? { ...e, segment: { ...e.segment, shootTimeSlot: prevSlot } } : e));
    }
  }

  async function handleRemoveTime(segmentId) {
    const prevSlot = localEntries.find(e => e.segment.id === segmentId)?.segment.shootTimeSlot ?? null;
    setLocalEntries(p => p.map(e => e.segment.id === segmentId
      ? { ...e, segment: { ...e.segment, shootTimeSlot: null } } : e));
    try { await onSegmentTimeSlot(segmentId, null); }
    catch {
      setLocalEntries(p => p.map(e => e.segment.id === segmentId
        ? { ...e, segment: { ...e.segment, shootTimeSlot: prevSlot } } : e));
    }
  }

  // 2026-09-03 (owner call): the arrows move the tile one 30-minute slot up
  // or down — not the old sort-order swap — skipping slots that already hold
  // another shoot. No-op at the edges of the day grid.
  function handleMoveSlot(segmentId, direction) {
    const entry = localEntries.find(e => e.segment.id === segmentId);
    const cur   = entry?.segment.shootTimeSlot;
    if (!cur) return;
    const step = direction === 'up' ? -1 : 1;
    let i = SCHEDULE_SLOTS.findIndex(s => s.value === cur);
    if (i === -1) return;
    for (i += step; i >= 0 && i < SCHEDULE_SLOTS.length; i += step) {
      const v = SCHEDULE_SLOTS[i].value;
      const occupied = localEntries.some(e => e.segment.id !== segmentId && e.segment.shootTimeSlot === v);
      if (!occupied) return handleDrop(v, segmentId);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', width: '92vw', maxWidth: 920, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx-primary)' }}>{prettyDate}</div>
            <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginTop: 2 }}>{localEntries.length} shoot{localEntries.length !== 1 ? 's' : ''} scheduled</div>
          </div>
          <button className="vc-close" onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left: unscheduled panel (segments on this day without a time slot) */}
          <div style={{ width: 210, flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px 6px', fontSize: 10, fontWeight: 700, color: 'var(--tx-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              No time set · {unscheduledE.length}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
              {unscheduledE.map(({ segment, rec }) => (
                <div
                  key={segment.id}
                  draggable
                  onDragStart={e => { e.dataTransfer.setData('segment-id', segment.id); }}
                  style={{ padding: '7px 10px', marginBottom: 4, background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'grab' }}
                >
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--violet)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                    {segment.segmentLabel}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cardTitle(rec)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <span className={`pill ${STATUS_PILL[rec.status] || 'pill-scripted'}`} style={{ fontSize: 9 }}>{rec.status}</span>
                    <span style={{ fontSize: 10, color: 'var(--tx-muted)' }}>{rec.client?.initials || ''}</span>
                    <button className="btn-ghost" style={{ fontSize: 9, padding: '1px 6px', marginLeft: 'auto' }} onClick={() => onOpenVideoCard(rec)}>↗</button>
                  </div>
                </div>
              ))}
              {unscheduledE.length === 0 && (
                <div style={{ padding: '8px 6px', fontSize: 12, color: 'var(--tx-faint)' }}>All shoots have a time.</div>
              )}
            </div>
          </div>

          {/* Right: time grid */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {SCHEDULE_SLOTS.map(slot => {
              const slotEntries = slotMap[slot.value] || [];
              const isDragOver  = dragOverSlot === slot.value;
              const isHour      = slot.value.endsWith(':00');
              return (
                <div
                  key={slot.value}
                  style={{ display: 'flex', alignItems: 'flex-start', minHeight: 40, borderBottom: `1px solid ${isHour ? 'var(--border)' : 'rgba(255,255,255,0.04)'}`, background: isDragOver ? 'rgba(139,109,255,0.08)' : 'transparent', transition: 'background 0.1s' }}
                  onDragOver={e => { e.preventDefault(); setDragOverSlot(slot.value); }}
                  onDragLeave={() => setDragOverSlot(null)}
                  onDrop={e => { e.preventDefault(); setDragOverSlot(null); const id = e.dataTransfer.getData('segment-id'); if (id) handleDrop(slot.value, id); }}
                >
                  <div style={{ width: 68, flexShrink: 0, padding: '10px 12px', fontSize: 10, color: isHour ? 'var(--tx-muted)' : 'var(--tx-faint)', fontWeight: isHour ? 600 : 400 }}>
                    {slot.label}
                  </div>
                  <div style={{ flex: 1, padding: '4px 8px 4px 0' }}>
                    {slotEntries.map(({ segment, rec }) => {
                      const conflict   = getTalentConflict({ segment });
                      const slotIdx    = SCHEDULE_SLOTS.findIndex(s => s.value === segment.shootTimeSlot);
                      const talentLine = (segment.talents || [])
                        .map(t => t.talent?.name || t.talentDisplayName || '')
                        .filter(Boolean)
                        .join(', ');
                      return (
                        <div
                          key={segment.id}
                          // Placed tiles are re-positionable by dragging them onto
                          // another slot (owner call 2026-09-03).
                          draggable
                          onDragStart={e => { e.dataTransfer.setData('segment-id', segment.id); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', marginBottom: 3, cursor: 'grab', background: conflict ? 'rgba(212,85,85,0.08)' : 'rgba(139,109,255,0.07)', border: `1px solid ${conflict ? 'rgba(212,85,85,0.3)' : 'rgba(139,109,255,0.2)'}`, borderRadius: 'var(--radius-sm)' }}
                        >
                          <div
                            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            title="Open production — edit details or view script"
                            onClick={() => onOpenVideoCard(rec)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenVideoCard(rec); } }}
                          >
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--violet)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {segment.segmentLabel}
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {cardTitle(rec)}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'flex', gap: 6, marginTop: 1 }}>
                              <span>{rec.client?.initials || ''}</span>
                              {talentLine && <span>· {talentLine}</span>}
                              {conflict && <span style={{ color: 'var(--s-incomplete-t)' }}>⚠ Talent conflict</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            {canReorder && (
                              <>
                                <button onClick={() => handleMoveSlot(segment.id, 'up')} disabled={slotIdx <= 0} title="Move up one half-hour (skips occupied slots)" style={{ padding: '2px 5px', fontSize: 10, background: 'none', border: '1px solid var(--border)', borderRadius: 3, cursor: slotIdx <= 0 ? 'default' : 'pointer', color: slotIdx <= 0 ? 'var(--tx-faint)' : 'var(--tx-secondary)', fontFamily: 'var(--font)' }}>↑</button>
                                <button onClick={() => handleMoveSlot(segment.id, 'down')} disabled={slotIdx >= SCHEDULE_SLOTS.length - 1} title="Move down one half-hour (skips occupied slots)" style={{ padding: '2px 5px', fontSize: 10, background: 'none', border: '1px solid var(--border)', borderRadius: 3, cursor: slotIdx >= SCHEDULE_SLOTS.length - 1 ? 'default' : 'pointer', color: slotIdx >= SCHEDULE_SLOTS.length - 1 ? 'var(--tx-faint)' : 'var(--tx-secondary)', fontFamily: 'var(--font)' }}>↓</button>
                              </>
                            )}
                            <button onClick={() => handleRemoveTime(segment.id)} title="Remove time slot" style={{ padding: '2px 5px', fontSize: 10, background: 'none', border: '1px solid var(--border)', borderRadius: 3, cursor: 'pointer', color: 'var(--tx-muted)', fontFamily: 'var(--font)' }}>✕</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
}

// ── Day Schedule View (Day tab) ────────────────────────────────────────────────
function DayScheduleView({ shootRecords, onOpenVideoCard, onUpdateTimeSlot, onReorder, unscheduled }) {
  const { user } = useAuth();
  const canReorder = canReorderShoots(user);
  const now = new Date();
  const [viewDate, setViewDate] = useState(() => dateKey(now));
  const todayKey = dateKey(now);

  function goPrev() {
    const [y, m, d] = viewDate.split('-').map(Number);
    setViewDate(dateKey(new Date(y, m - 1, d - 1)));
  }
  function goNext() {
    const [y, m, d] = viewDate.split('-').map(Number);
    setViewDate(dateKey(new Date(y, m - 1, d + 1)));
  }

  const [y, mo, d] = viewDate.split('-').map(Number);
  const prettyDate = new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const dayRecs = useMemo(() => {
    // Multi-day model: a project belongs to this day if any of its segments has shoot_day == viewDate.
    const filtered = shootRecords.filter(r =>
      (r.shootSegments || []).some(s => s.shootDay?.slice(0, 10) === viewDate)
    );
    return [...filtered].sort((a, b) => {
      if (a.shootOrder != null && b.shootOrder != null) return a.shootOrder - b.shootOrder;
      if (a.shootOrder != null) return -1;
      if (b.shootOrder != null) return 1;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [shootRecords, viewDate]);

  const [localRecs, setLocalRecs] = useState(dayRecs);
  useEffect(() => { setLocalRecs(dayRecs); }, [dayRecs]);

  async function handleMove(recId, direction) {
    const idx = localRecs.findIndex(r => r.id === recId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= localRecs.length) return;
    const reordered = [...localRecs];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const newOrder = reordered.map((r, i) => ({ ...r, shootOrder: i + 1 }));
    setLocalRecs(newOrder);
    try { await onReorder(viewDate, newOrder.map(r => ({ id: r.id, shootOrder: r.shootOrder }))); }
    catch { setLocalRecs(dayRecs); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="cal-nav-btn" onClick={goPrev}>‹</button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-primary)' }}>{prettyDate}</div>
        <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setViewDate(todayKey)} disabled={viewDate === todayKey}>Today</button>
        <button className="cal-nav-btn" onClick={goNext}>›</button>
      </div>

      {localRecs.length === 0 ? (
        <div style={{ color: 'var(--tx-faint)', fontSize: 14, padding: '20px 0' }}>No shoots scheduled for this day.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {localRecs.map((rec, i) => (
            <div key={rec.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--violet)', minWidth: 22, textAlign: 'center' }}>#{i + 1}</div>
              {rec.shootTimeSlot && (
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', minWidth: 68, fontVariantNumeric: 'tabular-nums' }}>{fmtSlot(rec.shootTimeSlot)}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {cardTitle(rec)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
                  {rec.client?.name}
                  {rec.shootLead && ` · ${rec.shootLead.name}`}
                  {rec.talent && ` · ${rec.talent.name}`}
                </div>
              </div>
              <span className={`pill ${STATUS_PILL[rec.status] || 'pill-scripted'}`}>{rec.status}</span>
              {canReorder && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => handleMove(rec.id, 'up')} disabled={i === 0} style={{ padding: '4px 7px', fontSize: 11, background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--tx-faint)' : 'var(--tx-secondary)', fontFamily: 'var(--font)' }}>↑</button>
                  <button onClick={() => handleMove(rec.id, 'down')} disabled={i === localRecs.length - 1} style={{ padding: '4px 7px', fontSize: 11, background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: i === localRecs.length - 1 ? 'default' : 'pointer', color: i === localRecs.length - 1 ? 'var(--tx-faint)' : 'var(--tx-secondary)', fontFamily: 'var(--font)' }}>↓</button>
                </div>
              )}
              <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => onOpenVideoCard(rec)}>View</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Talent calendar view ───────────────────────────────────────────────────────
function TalentCalendarView() {
  const { user } = useAuth();
  const assignTalent = canAssignTalent(user);
  const now    = new Date();
  const [year,            setYear]           = useState(now.getFullYear());
  const [month,           setMonth]          = useState(now.getMonth());
  const [byRange,         setByRange]        = useState({});
  const [loading,         setLoading]        = useState(false);
  const [selected,        setSelected]       = useState(null);
  const [allTalent,       setAllTalent]      = useState([]);
  const [editingTalentId, setEditingTalentId] = useState('');
  const [editingRows,     setEditingRows]    = useState([]);
  const [editLoading,     setEditLoading]    = useState(false);
  const [modal,           setModal]          = useState(null); // { dateStr, existing }
  const [shootsModal,     setShootsModal]    = useState(null); // { talent, dateStr, segments, loading }
  const [availabilityTalent, setAvailabilityTalent] = useState(null); // { id, name }
  // Booking model (owner call 2026-09-01): day cells always show AVAILABLE
  // talent in soft golden yellow; clicking one offers a Book button. Booked
  // talent (explicit bookings + talent on shoot segments that day) render
  // GREEN, and the booked set feeds the double-click script matcher.
  const [segsByDate,     setSegsByDate]     = useState({}); // { 'YYYY-MM-DD': [{id,name,initials}] }
  const [bookingsByDate, setBookingsByDate] = useState({}); // { 'YYYY-MM-DD': [{id,talentId,name,initials}] }
  const [bookPopover,    setBookPopover]    = useState(null); // { talent:{id,name}, dateStr, bookingId|null }
  const [bookingBusy,    setBookingBusy]    = useState(false);
  const [bookingsTick,   setBookingsTick]   = useState(0);
  // Double-click a browse-mode day → shootable-scripts modal for that day
  // (2026-09-02 overhaul: it schedules ASSIGNED segments via the Schedule
  // button; the plan modal now lives on the Script List's Ready tab).
  const [dayMatches, setDayMatches] = useState(null); // 'YYYY-MM-DD' | null
  const [segsTick,   setSegsTick]   = useState(0);    // bump to re-pull booked chips
  // Producer day notes ("theme of the day") shown in each day-cell header.
  const [notesByDate, setNotesByDate] = useState({});
  const [noteEdit,    setNoteEdit]    = useState(null); // { dateStr, value } | null
  const [noteBusy,    setNoteBusy]    = useState(false);
  // Busy days expand in place (owner call 2026-09-15): "+N more ▾" was an inert
  // count — it now unfolds every talent chip for that day so the hidden ones
  // can be clicked to book/schedule; "Show less ▴" folds back. Mirrors the
  // Shoot Schedule grid's expandedDays pattern.
  const [expandedDays, setExpandedDays] = useState(() => new Set());
  const expandDay   = useCallback(key => setExpandedDays(prev => new Set(prev).add(key)), []);
  const collapseDay = useCallback(key => setExpandedDays(prev => { const n = new Set(prev); n.delete(key); return n; }), []);

  const openShootsForTalent = useCallback(async (talent, dateStr) => {
    setShootsModal({ talent, dateStr, segments: [], loading: true });
    try {
      const r = await api.get(`/api/shoot-segments?talentId=${talent.id}&shootDay=${dateStr}`);
      setShootsModal({ talent, dateStr, segments: r.data, loading: false });
    } catch {
      setShootsModal({ talent, dateStr, segments: [], loading: false });
    }
  }, []);

  const todayKey = dateKey(now);
  const days     = useMemo(() => buildCalDays(year, month), [year, month]);

  useEffect(() => {
    api.get('/api/talent').then(r => setAllTalent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/talent-availability/by-range?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => setByRange(r.data))
      .catch(() => setByRange({}))
      .finally(() => setLoading(false));
  }, [year, month]);

  // Scheduled talent per day for the visible month (solid chips).
  useEffect(() => {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/shoot-segments?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => {
        const map = {};
        for (const seg of r.data) {
          const k = seg.shootDay ? seg.shootDay.slice(0, 10) : null;
          if (!k) continue;
          for (const t of (seg.talents || [])) {
            if (!t.talent) continue;
            if (!map[k]) map[k] = new Map();
            if (!map[k].has(t.talent.id)) {
              map[k].set(t.talent.id, { id: t.talent.id, name: t.talent.name, initials: t.talent.initials });
            }
          }
        }
        const out = {};
        for (const [k, m] of Object.entries(map)) out[k] = [...m.values()];
        setSegsByDate(out);
      })
      .catch(() => setSegsByDate({}));
  }, [year, month, segsTick]);

  // Day-level bookings for the visible month (green chips).
  useEffect(() => {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/talent-bookings?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => setBookingsByDate(r.data || {}))
      .catch(() => setBookingsByDate({}));
  }, [year, month, bookingsTick]);

  // Producer day notes for the visible month (grid spans adjacent months).
  useEffect(() => {
    api.get(`/api/tal/day-notes?from=${dateKey(days[0])}&to=${dateKey(days[days.length - 1])}`)
      .then(r => setNotesByDate(r.data || {}))
      .catch(() => setNotesByDate({}));
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveDayNote() {
    if (!noteEdit) return;
    setNoteBusy(true);
    try {
      const r = await api.put(`/api/tal/day-notes/${noteEdit.dateStr}`, { note: noteEdit.value });
      setNotesByDate(prev => {
        const next = { ...prev };
        if (r.data.note) next[noteEdit.dateStr] = r.data.note;
        else delete next[noteEdit.dateStr];
        return next;
      });
      setNoteEdit(null);
    } catch (e) { alert(e.response?.data?.error || 'Saving the day note failed.'); }
    finally { setNoteBusy(false); }
  }

  async function handleBook() {
    if (!bookPopover) return;
    setBookingBusy(true);
    try {
      await api.post('/api/talent-bookings', { talentId: bookPopover.talent.id, date: bookPopover.dateStr });
      setBookPopover(null);
      setBookingsTick(t => t + 1);
    } catch (e) { alert(e.response?.data?.error || 'Booking failed.'); }
    finally { setBookingBusy(false); }
  }
  async function handleUnbook() {
    if (!bookPopover?.bookingId) return;
    setBookingBusy(true);
    try {
      await api.delete(`/api/talent-bookings/${bookPopover.bookingId}`);
      setBookPopover(null);
      setBookingsTick(t => t + 1);
    } catch (e) { alert(e.response?.data?.error || 'Unbooking failed.'); }
    finally { setBookingBusy(false); }
  }

  useEffect(() => {
    if (!editingTalentId) { setEditingRows([]); return; }
    setEditLoading(true);
    api.get(`/api/talent-availability?talentId=${editingTalentId}`)
      .then(r => setEditingRows(r.data))
      .catch(() => setEditingRows([]))
      .finally(() => setEditLoading(false));
  }, [editingTalentId]);

  // Expand editing talent's rows into a per-date map for the visible month
  const editingByDate = useMemo(() => {
    if (!editingTalentId || !editingRows.length) return {};
    const map = {};
    for (const day of days) {
      if (day.getMonth() !== month) continue;
      const key = dateKey(day);
      const dow = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())).getUTCDay();
      for (const row of editingRows) {
        const rowDate = row.date ? row.date.slice(0, 10) : null;
        if (!row.isRecurring) {
          if (rowDate === key) { map[key] = row; break; }
        } else {
          if (row.recurrenceDayOfWeek === dow &&
              rowDate && rowDate <= key &&
              (!row.recurrenceEndDate || row.recurrenceEndDate.slice(0, 10) >= key)) {
            map[key] = row; break;
          }
        }
      }
    }
    return map;
  }, [editingTalentId, editingRows, days, month]);

  function reloadBrowseRange() {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/talent-availability/by-range?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => setByRange(r.data)).catch(() => {});
  }
  function reloadEditingRows() {
    if (!editingTalentId) return;
    api.get(`/api/talent-availability?talentId=${editingTalentId}`)
      .then(r => setEditingRows(r.data)).catch(() => {});
  }

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
  }
  function goToday() { setYear(now.getFullYear()); setMonth(now.getMonth()); }

  function prettyDate(key) {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  const isEditMode      = Boolean(editingTalentId);
  const editingName     = isEditMode ? (allTalent.find(t => t.id === editingTalentId)?.name || '') : '';
  const selectedAvail   = selected ? (byRange[selected] || []) : [];
  const availableCount  = selected ? selectedAvail.length : 0;
  // Booked on the selected day = explicit booking OR on a shoot segment.
  const selectedBookedIds = new Set([
    ...(selected ? (segsByDate[selected] || []).map(t => t.id) : []),
    ...(selected ? (bookingsByDate[selected] || []).map(b => b.talentId) : []),
  ]);

  return (
    <div className="cal-grid-wrap">
      {/* Header row: month nav + talent selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
          <div className="cal-month-label">{MONTH_NAMES[month]} {year}</div>
          <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={goToday}>Today</button>
          <button className="cal-nav-btn" onClick={nextMonth}>›</button>
        </div>
        {allTalent.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {!isEditMode && (
              <span style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                <span style={{ color: GOLD.text }}>● available</span>
                <span style={{ color: BOOKG.text }}>● booked</span>
              </span>
            )}
            <label style={{ fontSize: 12, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>Scheduling for:</label>
            <select
              className="form-input"
              style={{ fontSize: 12, padding: '5px 10px', width: 190 }}
              value={editingTalentId}
              onChange={e => { setEditingTalentId(e.target.value); setSelected(null); }}
            >
              <option value="">— Browse all —</option>
              {allTalent.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Browse mode chip pool — sits ABOVE the calendar window (owner call,
          2026-09-01: names on top, availability counts in the day cells). */}
      {!isEditMode && !loading && (
        <div style={{ marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 10 }}>
            {!selected
              ? 'Click a date to see talent availability — golden = available, green = booked'
              : `${prettyDate(selected)} · ${availableCount} of ${allTalent.length} available · ${selectedBookedIds.size} booked`}
          </div>
          {allTalent.length === 0 ? (
            <div style={{ color: 'var(--tx-faint)', fontSize: 12 }}>No talent accounts yet.</div>
          ) : (
            <div className="eq-unscheduled-list">
              {allTalent.map(t => {
                const noColor  = !selected;
                const avEntry  = noColor ? null : selectedAvail.find(av => av.talentId === t.id);
                const isAvail  = Boolean(avEntry);
                const isBooked = !noColor && selectedBookedIds.has(t.id);
                // Color language: green = booked, golden = available-unbooked,
                // red = not available on the selected day.
                const accentColor = noColor ? 'var(--border)'
                  : isBooked ? BOOKG.border
                  : isAvail  ? GOLD.border
                  : 'rgba(212,85,85,0.45)';
                const chipBg = noColor ? 'transparent'
                  : isBooked ? BOOKG.bg
                  : isAvail  ? GOLD.bg
                  : 'rgba(212,85,85,0.06)';
                const textColor = noColor ? 'var(--tx-secondary)'
                  : isBooked ? BOOKG.text
                  : isAvail  ? GOLD.text
                  : 'var(--s-incomplete-t)';
                const label = isBooked
                  ? `${t.name} — Booked`
                  : isAvail
                    ? (avEntry.availabilityType === 'full_day'
                        ? `${t.name} — All day`
                        : `${t.name} — ${fmtTime12(avEntry.blockStart)} to ${fmtTime12(avEntry.blockEnd)}`)
                    : t.name;
                return (
                  <div
                    key={t.id}
                    className="eq-chip eq-chip-flat"
                    onClick={assignTalent ? (() => setAvailabilityTalent({ id: t.id, name: t.name })) : undefined}
                    title={assignTalent ? 'Click to edit availability for this talent' : undefined}
                    style={{ borderLeftColor: accentColor, background: chipBg, cursor: assignTalent ? 'pointer' : 'default' }}
                  >
                    <span className="eq-chip-title" style={{ color: textColor }}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="cal-loading">Loading…</div>
      ) : (
        <div className="cal-grid">
          {DAY_NAMES.map(d => <div key={d} className="cal-dow">{d}</div>)}
          {days.map(day => {
            const key     = dateKey(day);
            const isToday = key === todayKey;
            const otherMo = day.getMonth() !== month;

            if (isEditMode) {
              const entry    = editingByDate[key];
              const hasEntry = Boolean(entry) && !otherMo;
              return (
                <div
                  key={key}
                  className={`cal-day ${isToday ? 'today' : ''} ${otherMo ? 'other-month' : ''}`}
                  style={{
                    cursor: otherMo ? 'default' : 'pointer',
                    background: hasEntry ? 'rgba(139,109,255,0.1)' : undefined,
                  }}
                  onClick={() => { if (!otherMo) setModal({ dateStr: key, existing: entry || null }); }}
                >
                  <div className="cal-day-num" style={{ color: hasEntry ? 'var(--violet)' : undefined }}>
                    {day.getDate()}
                  </div>
                  {hasEntry && (
                    <div style={{ fontSize: 9, color: 'var(--violet)', padding: '0 3px', lineHeight: 1.4 }}>
                      {entry.availabilityType === 'full_day'
                        ? 'All day'
                        : `${fmtTime12(entry.blockStart)}–${fmtTime12(entry.blockEnd)}`}
                      {entry.isRecurring && ' ↻'}
                    </div>
                  )}
                  {!hasEntry && !otherMo && (
                    <div style={{ fontSize: 9, color: 'var(--tx-faint)', padding: '0 3px', opacity: 0.4 }}>+ add</div>
                  )}
                </div>
              );
            }

            // Browse mode: BOOKED talent (segment-scheduled or day-booked)
            // render GREEN; available-but-unbooked render soft GOLDEN and
            // clicking one offers the Book button.
            const availEntries = otherMo ? [] : (byRange[key] || []);
            const schedTalents = otherMo ? [] : (segsByDate[key] || []);
            const dayBookings  = otherMo ? [] : (bookingsByDate[key] || []);
            const schedIds     = new Set(schedTalents.map(t => t.id));
            const bookedIds    = new Set([...schedIds, ...dayBookings.map(b => b.talentId)]);
            const goldEntries  = availEntries.filter(av => !bookedIds.has(av.talentId));
            const count   = bookedIds.size + goldEntries.length;
            const isSel   = selected === key;
            const MAX_TALENT_CHIPS = 5;
            const combined = [
              ...schedTalents.map(t => ({ kind: 'shooting', key: `s-${t.id}`, talent: t })),
              ...dayBookings.filter(b => !schedIds.has(b.talentId))
                            .map(b => ({ kind: 'booked', key: `b-${b.talentId}`, booking: b })),
              ...goldEntries.map(av => ({ kind: 'available', key: av.id || `a-${av.talentId}`, av })),
            ];
            const isExpanded = expandedDays.has(key);
            const visible  = isExpanded ? combined : combined.slice(0, MAX_TALENT_CHIPS);
            const overflow = combined.length - MAX_TALENT_CHIPS;
            return (
              <div
                key={key}
                className={`cal-day ${isToday ? 'today' : ''} ${otherMo ? 'other-month' : ''}`}
                style={{
                  cursor: otherMo ? 'default' : 'pointer',
                  background: isSel ? 'rgba(139,109,255,0.14)' : count > 0 ? 'rgba(139,109,255,0.04)' : undefined,
                  outline: isSel ? '2px solid var(--violet)' : undefined,
                  outlineOffset: '-1px',
                }}
                onClick={() => { if (!otherMo) setSelected(isSel ? null : key); }}
                onDoubleClick={() => { if (!otherMo) { setSelected(key); setDayMatches(key); } }}
                title={otherMo ? undefined : 'Double-click: scripts this day’s BOOKED talent can shoot'}
              >
                {/* 2026-09-02 overhaul: the availability-count badge is gone —
                    the header now carries the producer's day note ("theme of
                    the day") so anyone glancing at the calendar knows what's
                    shooting. Producers click the note area to edit. */}
                <div className="cal-day-num-wrap" style={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
                  <span className="cal-day-num" style={{ color: count > 0 ? 'var(--violet)' : undefined, flexShrink: 0 }}>
                    {day.getDate()}
                  </span>
                  {!otherMo && (notesByDate[key] || assignTalent) && (
                    <span
                      title={notesByDate[key]
                        ? (assignTalent ? `${notesByDate[key]} — click to edit the day note` : notesByDate[key])
                        : 'Add a day note — what shoots this day'}
                      onClick={e => {
                        if (!assignTalent) return;
                        e.stopPropagation();
                        setNoteEdit({ dateStr: key, value: notesByDate[key] || '' });
                      }}
                      style={{
                        fontSize: 9, lineHeight: 1.25, fontStyle: 'italic',
                        color: notesByDate[key] ? 'var(--violet)' : 'var(--tx-faint)',
                        cursor: assignTalent ? 'pointer' : 'default',
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        opacity: notesByDate[key] ? 1 : 0.45, minWidth: 0, flex: 1,
                      }}
                    >
                      {notesByDate[key] || '✎'}
                    </span>
                  )}
                </div>
                {count > 0 && (
                  <div className="cal-events">
                    {visible.map(item => {
                      if (item.kind === 'shooting') {
                        const t = item.talent;
                        return (
                          <div
                            key={item.key}
                            className="cal-talent-chip"
                            title={`${t.name} is booked on a shoot — click to see which`}
                            onClick={e => { e.stopPropagation(); openShootsForTalent(t, key); }}
                            style={{
                              cursor: 'pointer',
                              background: BOOKG.bg,
                              border: `1px solid ${BOOKG.border}`,
                            }}
                          >
                            <span className="cal-talent-name" style={{ color: BOOKG.text }}>{t.name}</span>
                            <span className="cal-talent-time" style={{ color: BOOKG.text }}>Shooting</span>
                          </div>
                        );
                      }
                      if (item.kind === 'booked') {
                        const b = item.booking;
                        return (
                          <div
                            key={item.key}
                            className="cal-talent-chip"
                            title={assignTalent
                              ? `${b.name} is booked to work this day — click to manage the booking`
                              : `${b.name} is booked to work this day`}
                            onClick={e => {
                              e.stopPropagation();
                              if (assignTalent) setBookPopover({ talent: { id: b.talentId, name: b.name }, dateStr: key, bookingId: b.id });
                            }}
                            style={{
                              cursor: assignTalent ? 'pointer' : 'default',
                              background: BOOKG.bg,
                              border: `1px solid ${BOOKG.border}`,
                            }}
                          >
                            <span className="cal-talent-name" style={{ color: BOOKG.text }}>{b.name}</span>
                            <span className="cal-talent-time" style={{ color: BOOKG.text }}>Booked</span>
                          </div>
                        );
                      }
                      const av      = item.av;
                      const tal     = allTalent.find(t => t.id === av.talentId);
                      const name    = tal?.name || 'Unknown';
                      const timeStr = av.availabilityType === 'full_day'
                        ? 'All day'
                        : `${fmtTime12(av.blockStart)}–${fmtTime12(av.blockEnd)}`;
                      return (
                        <div
                          key={item.key}
                          className="cal-talent-chip"
                          title={assignTalent
                            ? `${name} is available (${timeStr}) — click to book them for this day`
                            : `${name} is available (${timeStr})`}
                          onClick={e => {
                            e.stopPropagation();
                            if (assignTalent && tal) setBookPopover({ talent: { id: tal.id, name: tal.name }, dateStr: key, bookingId: null });
                            else if (tal) openShootsForTalent(tal, key);
                          }}
                          style={{
                            cursor: 'pointer',
                            background: GOLD.bg,
                            border: `1px solid ${GOLD.border}`,
                          }}
                        >
                          <span className="cal-talent-name" style={{ color: GOLD.text }}>{name}</span>
                          <span className="cal-talent-time" style={{ color: GOLD.text }}>{timeStr}</span>
                        </div>
                      );
                    })}
                    {!isExpanded && overflow > 0 && (
                      <div
                        className="cal-overflow"
                        role="button"
                        tabIndex={0}
                        title={`Show all ${combined.length} talent for this day`}
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); expandDay(key); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); expandDay(key); } }}
                      >+{overflow} more ▾</div>
                    )}
                    {isExpanded && overflow > 0 && (
                      <div
                        className="cal-overflow"
                        role="button"
                        tabIndex={0}
                        title="Collapse back to the first few"
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); collapseDay(key); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); collapseDay(key); } }}
                      >Show less ▴</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit mode status bar */}
      {isEditMode && !loading && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(139,109,255,0.07)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(139,109,255,0.2)' }}>
          {editLoading
            ? <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>Loading {editingName}'s availability…</span>
            : <span style={{ fontSize: 12, color: 'var(--violet)' }}>
                Editing <strong>{editingName}</strong> — click any date to add or edit availability
              </span>
          }
        </div>
      )}

      {shootsModal && (
        <TalentShootsModal
          talent={shootsModal.talent}
          dateStr={shootsModal.dateStr}
          segments={shootsModal.segments}
          loading={shootsModal.loading}
          onClose={() => setShootsModal(null)}
        />
      )}

      {dayMatches && (
        <DayMatchesModal
          dateStr={dayMatches}
          canSchedule={assignTalent}
          onScheduled={() => setSegsTick(t => t + 1)}
          onClose={() => setDayMatches(null)}
        />
      )}

      {/* Day-note editor — the producer's "theme of the day" header note. */}
      {noteEdit && (
        <div className="modal-overlay" onClick={() => setNoteEdit(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Day note — {prettyDate(noteEdit.dateStr)}</div>
              <button className="vc-close" onClick={() => setNoteEdit(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 10 }}>
                What's the theme of the day? Everyone who opens this calendar sees it —
                e.g. “We're doing PG today” or “TPK and PG”.
              </div>
              <textarea
                autoFocus
                className="form-input"
                rows={3}
                maxLength={500}
                style={{ width: '100%', fontSize: 13, resize: 'vertical' }}
                value={noteEdit.value}
                onChange={e => setNoteEdit({ ...noteEdit, value: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveDayNote(); }}
              />
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--tx-faint)', alignSelf: 'center' }}>
                Empty note removes it.
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setNoteEdit(null)}>Cancel</button>
                <button className="btn-primary" onClick={saveDayNote} disabled={noteBusy}>
                  {noteBusy ? 'Saving…' : 'Save note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book / unbook popover — from a golden (available) or green (booked)
          name chip on a day cell. */}
      {bookPopover && (
        <div className="modal-overlay" onClick={() => setBookPopover(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 360 }}>
            <div className="modal-header">
              <div className="modal-title">{bookPopover.talent.name}</div>
              <button className="vc-close" onClick={() => setBookPopover(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 10 }}>{prettyDate(bookPopover.dateStr)}</div>
              <div style={{ fontSize: 13, color: bookPopover.bookingId ? BOOKG.text : GOLD.text }}>
                {bookPopover.bookingId
                  ? 'Booked to work this day.'
                  : 'Available this day — book them to lock the day in.'}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setBookPopover(null)}>Cancel</button>
              {bookPopover.bookingId ? (
                <button
                  className="btn-ghost"
                  style={{ color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.35)' }}
                  onClick={handleUnbook}
                  disabled={bookingBusy}
                >
                  {bookingBusy ? 'Removing…' : 'Remove Booking'}
                </button>
              ) : (
                <button
                  className="btn-primary"
                  style={{ background: BOOKG.text, color: '#07070c' }}
                  onClick={handleBook}
                  disabled={bookingBusy}
                >
                  {bookingBusy ? 'Booking…' : 'Book ✓'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <StaffAvailabilityModal
          dateStr={modal.dateStr}
          existing={modal.existing}
          forTalentId={editingTalentId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reloadEditingRows(); reloadBrowseRange(); }}
          onDeleted={() => { setModal(null); reloadEditingRows(); reloadBrowseRange(); }}
        />
      )}

      {availabilityTalent && (
        <div
          className="modal-overlay"
          onClick={() => { setAvailabilityTalent(null); reloadBrowseRange(); }}
        >
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 760, maxWidth: '95vw' }}>
            <div className="modal-header">
              <div className="modal-title">Availability — {availabilityTalent.name}</div>
              <button
                className="vc-close"
                onClick={() => { setAvailabilityTalent(null); reloadBrowseRange(); }}
              >✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 12 }}>
                Click any date to mark or edit availability on behalf of this talent.
              </div>
              <TalentAvailabilityView talentId={availabilityTalent.id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const VIEWS = [
  { key: 'month', label: 'Month' },
  { key: 'week',  label: 'Week' },
  { key: 'day',   label: 'Day' },
];

// Tab order mirrors the workflow (owner call, 2026-09-01): you have a Script
// List, then you check what talent is available, then you put them on the
// Shoot Schedule. Script List is also the landing tab for the same reason.
const PAGE_TABS = [
  { key: 'assign', label: 'Script List' },
  { key: 'talent', label: 'Talent Calendar' },
  { key: 'shoot',  label: 'Shoot Schedule' },
];


// ── Script List view (Production Calendar tab) ───────────────────────────────
// Replaces the Assignment List. Membership is DERIVED (D-050): Ready = Skit/
// Mockumentary in Needs Review or Approved with no shoot segments; Scheduled =
// has segments (including Riff cards). Readable by the whole tab-bar role set;
// write actions render only for Producer/Power User/Admin and are enforced
// server-side regardless.

function ScriptListView({ focus, onFocusConsumed, onOpenShootDay, onOpenTalentCalendar, initialTab, onOpenCard, onScheduled }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite   = canAssignTalent(user);       // Producer / Power User / Admin

  // 'assigned' replaced 'scheduled' in the 2026-09-02 overhaul; old back-tickets
  // may still carry 'scheduled', so both land on the Assigned tab.
  const [activeTab,   setActiveTab]   = useState(['scheduled', 'assigned'].includes(initialTab) ? 'assigned' : 'ready'); // 'ready' | 'assigned'
  const [clients,     setClients]     = useState([]);
  // v2 key (2026-09-03): older saved filters pre-date the All-projects default
  // and kept hiding other projects' rows — start everyone fresh once.
  const [clientId,    setClientId]    = useState(() => localStorage.getItem('sl_client_v2') ?? null);
  const [availableOn, setAvailableOn] = useState('');
  const [showHidden,  setShowHidden]  = useState(false);
  const [q,           setQ]           = useState('');
  const [page,        setPage]        = useState(1);
  const [data,        setData]        = useState({ rows: [], total: 0, pageSize: 50 });
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [planFor,     setPlanFor]     = useState(null); // scriptId being scheduled
  const [notesEdit,   setNotesEdit]   = useState(null); // { liveProductionId, value }
  const focusRef = useRef(null);

  // D-057: filter options are derived from the DATA, not is_active — the API's
  // clients facet lists every client with ≥1 row in the current tab (keeping
  // inactive clients that hold real work, dropping zero-row stubs).
  // 2026-09-03 (owner report): first visit now defaults to ALL projects — the
  // old first-client default silently hid other projects' rows (Ready was
  // ~570 rows when that default was chosen; Approved-only it's small enough).
  useEffect(() => {
    if (clientId !== null) return;
    api.get('/api/tal?status=ready&pageSize=1').then(r => {
      setClients(r.data.clients || []);
      setClientId(prev => (prev !== null ? prev : ''));
    }).catch(() => setClientId(''));
  }, [clientId]);

  // A Detail jump from the peek card: land on the Scheduled tab, no filters,
  // one large page, and scroll the row into view.
  useEffect(() => {
    if (!focus) return;
    setActiveTab(focus.tab === 'ready' ? 'ready' : 'assigned');
    setClientId('');
    setQ('');
    setPage(1);
  }, [focus]);

  const load = useCallback(async () => {
    if (clientId === null) return; // clients not resolved yet
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: activeTab, page: String(page) });
      if (focus) params.set('pageSize', '200');
      if (clientId) params.set('clientId', clientId);
      if (q.trim()) params.set('q', q.trim());
      if (activeTab === 'ready') {
        if (availableOn) params.set('availableOn', availableOn);
        if (showHidden)  params.set('showHidden', '1');
      }
      const r = await api.get(`/api/tal?${params.toString()}`);
      setData(r.data);
      if (Array.isArray(r.data.clients)) setClients(r.data.clients);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load the Script List.');
    } finally { setLoading(false); }
  }, [activeTab, clientId, availableOn, showHidden, q, page, focus]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (focus && !loading && focusRef.current) {
      focusRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const t = setTimeout(() => onFocusConsumed && onFocusConsumed(), 2500);
      return () => clearTimeout(t);
    }
  }, [focus, loading, data, onFocusConsumed]);

  function pickClient(id) {
    setClientId(id);
    setPage(1);
    localStorage.setItem('sl_client_v2', id);
  }

  // Opening a script from here hands the editor a return ticket (backTo): its
  // top-left back button comes back to THIS tab of the Production Calendar's
  // Script List instead of the Scripts section list view.
  function openInEditor(scriptId) {
    navigate('/scripts', {
      state: {
        openScriptId: scriptId,
        backTo: {
          path:  '/calendar',
          state: { calendarInit: { pageTab: 'assign', scriptListTab: activeTab } },
          label: '← Script List',
        },
      },
    });
  }

  async function handleHide(scriptId) {
    if (!window.confirm('Hide this script from the Ready tab? It stays schedulable and can be recovered with "Show hidden".')) return;
    try { await api.patch(`/api/tal/${scriptId}/remove`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Hide failed.'); }
  }

  async function handleUnhide(scriptId) {
    try { await api.patch(`/api/tal/${scriptId}/restore`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Unhide failed.'); }
  }

  async function saveNotes() {
    if (!notesEdit) return;
    try {
      await api.patch(`/api/tal/lp/${notesEdit.liveProductionId}/notes`, { notes: notesEdit.value });
      setNotesEdit(null);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Save failed.'); }
  }

  function fmtDate(dateStr) {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  const QUAL_PILL = {
    'Needs Review': { bg: 'rgba(255,200,80,0.14)', fg: 'rgba(255,200,80,0.95)' },
    'Approved':     { bg: 'rgba(74,222,128,0.12)', fg: '#4ade80' },
  };

  function TalentChips({ row }) {
    if (row.noTalent) {
      return (
        <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontStyle: 'italic' }}>
          No talent assigned
        </span>
      );
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {row.resolvedTalents.map(t => (
          <span
            key={t.id}
            title={`${t.name} — open the Talent Calendar`}
            onClick={() => onOpenTalentCalendar && onOpenTalentCalendar()}
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              padding: '2px 6px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              background: 'var(--bg-elevated)', color: 'var(--tx-primary)',
              border: '1px solid var(--border)',
            }}
          >
            {t.initials || t.name}
          </span>
        ))}
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.pageSize || 50)));

  // Latent crash fixed in Run 5 (present since Run 3, DEF-096): switching
  // sub-tabs re-renders once with the PREVIOUS tab's rows still in state, and
  // the two row shapes differ (a Ready row has no shootDays; a Scheduled row
  // has no resolvedTalents). Render only rows fetched for the active tab.
  const tabRows = data.status === activeTab ? data.rows : [];

  // ── D-120 Longform hierarchy: ONE Assigned row per series, episodes as children ──
  const [expandedSeries, setExpandedSeries] = useState(() => new Set());
  const toggleSeries = (scriptId) => setExpandedSeries(prev => { const n = new Set(prev); n.has(scriptId) ? n.delete(scriptId) : n.add(scriptId); return n; });
  const rowHasFocus = (row) => !!focus?.liveProductionId && (row.liveProductionId === focus.liveProductionId || (row.episodes || []).some(e => e.liveProductionId === focus.liveProductionId));
  const isSeriesExpanded = (row) => expandedSeries.has(row.scriptId) || rowHasFocus(row);
  const renderEpisodeChild = (ep, series) => (
    <div
      key={ep.liveProductionId}
      data-testid="sl-episode-row"
      className="sl-episode-row"
      ref={focus?.liveProductionId === ep.liveProductionId ? focusRef : undefined}
      style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 1.4fr 1.8fr auto', gap: 12, alignItems: 'center', padding: '7px 14px 7px 48px', background: 'rgba(255,255,255,0.015)', border: focus?.liveProductionId === ep.liveProductionId ? '1px solid var(--violet)' : '1px solid var(--border)', borderTop: 'none', borderRadius: 'var(--radius-sm)' }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => onOpenCard && onOpenCard(ep.liveProductionId)} title={`${series.title} · Ep ${ep.episodeNumber} — open the production`}>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--violet)', marginRight: 8 }}>Ep {ep.episodeNumber}</span>{ep.episodeTitle || `Episode ${ep.episodeNumber}`}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{series.client?.name || '—'}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {ep.filmedCount > 0 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: 'var(--s-filmed)', color: 'var(--s-filmed-t)' }}>{ep.filmedCount}/{ep.totalSegments} filmed</span>}
        {ep.shootDays.length === 0 ? <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontStyle: 'italic' }}>No shoot day set</span>
          : ep.shootDays.map(d => <span key={d} style={{ fontSize: 11, color: 'var(--violet)', textDecoration: 'underline dotted', cursor: 'pointer' }} onClick={() => onOpenShootDay && onOpenShootDay(d)}>{fmtDate(d)}</span>)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {[...new Map((ep.segments || []).flatMap(sg => sg.talents || []).map(t => [t.talentId || `n:${t.name}`, t])).values()].map(t => (
          <span key={t.talentId || `n:${t.name}`} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 999, background: t.covered === false ? 'rgba(212,85,85,0.18)' : 'var(--bg-hover)', color: t.covered === false ? 'var(--s-incomplete-t)' : 'var(--tx-secondary)', border: '1px solid var(--border)' }}>{t.initials || t.name}</span>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--tx-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.notes || ''}</div>
      <div />
    </div>
  );
  const renderAssignedRows = (row) => {
    const main = (
            <div
              key={row.isSeries ? `series:${row.scriptId}` : row.liveProductionId}
              ref={rowHasFocus(row) ? focusRef : undefined}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1.1fr 1.4fr 1.8fr auto',
                gap: 12, alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--bg-card)',
                border: rowHasFocus(row) ? '1px solid var(--violet)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                {/* 2026-09-02 (owner call): clicking an Assigned script opens
                    its Video Card in place — same as the old Open → button,
                    which is gone. Scheduling happens from the Talent Calendar. */}
                {row.isSeries ? (
                  <div
                    data-testid="sl-series-row"
                    data-episodes={row.episodeCount}
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 8 }}
                    onClick={() => toggleSeries(row.scriptId)}
                    title={`${row.title} — ${row.episodeCount} episode${row.episodeCount === 1 ? '' : 's'} assigned · click to ${isSeriesExpanded(row) ? 'collapse' : 'expand'}`}
                  >
                    <span style={{ color: 'var(--tx-faint)', fontSize: 11, width: 12 }}>{isSeriesExpanded(row) ? '▾' : '▸'}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title}</span>
                    <span className="format-badge lf" style={{ fontSize: 9 }}>LF</span>
                    <span style={{ fontSize: 10, color: 'var(--tx-muted)', fontWeight: 400 }}>{row.episodeCount} episode{row.episodeCount === 1 ? '' : 's'}</span>
                  </div>
                ) : (
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  onClick={() => onOpenCard && onOpenCard(row.liveProductionId)}
                  title={`${row.title} — open the production`}
                >
                  {row.title}
                  {row.isRiff && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--tx-muted)', fontWeight: 400 }}>(Riff)</span>}
                </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>{row.client?.name || '—'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                {row.filmedCount > 0 && (
                  <span
                    title={`${row.filmedCount} of ${row.totalSegments} segments filmed — the script leaves Assigned when all are filmed`}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-sm)',
                      background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80',
                    }}
                  >
                    {row.filmedCount}/{row.totalSegments} filmed
                  </span>
                )}
                {row.shootDays.length === 0 ? (
                  <span
                    title="Segments are assigned but no shoot day is set yet — book talent on the Talent Calendar, then Schedule from the day's Shootable Scripts"
                    style={{ fontSize: 11, color: 'rgba(255,200,80,0.95)', fontStyle: 'italic' }}
                  >
                    No shoot day set
                  </span>
                )
                  : row.shootDays.map(d => (
                    <span
                      key={d}
                      onClick={() => onOpenShootDay && onOpenShootDay(d)}
                      title="Open this day on the Shoot Schedule"
                      style={{
                        fontSize: 11, color: 'var(--violet)', cursor: 'pointer',
                        textDecoration: 'underline', textUnderlineOffset: 2,
                      }}
                    >
                      {fmtDate(d)}
                    </span>
                  ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(() => {
                  // Run 5 (D-060): a booked talent uncovered on a future shoot
                  // day renders RED with the reason on hover — state-based,
                  // deliberate overrides included. Red says what is; the alert
                  // email says what changed.
                  const seen = new Map(); // key -> { t, broken: [{day, cov}] }
                  for (const seg of row.segments) {
                    for (const t of seg.talents) {
                      const key = t.talentId || `n:${t.name}`;
                      if (!t.name) continue;
                      if (!seen.has(key)) seen.set(key, { t, brokenDays: [] });
                      if (t.covered === false && seg.shootDay) seen.get(key).brokenDays.push({ day: seg.shootDay, cov: t.coverage });
                    }
                  }
                  const chips = [...seen.values()].map(({ t, brokenDays }) => {
                    const broken = brokenDays.length > 0;
                    return (
                      <span
                        key={t.talentId || `n:${t.name}`}
                        title={broken
                          ? `⚠ ${brokenDays.map(b => coverageReason(t.name, `on ${b.day}`, b.cov)).join('\n')}\nRe-time, re-plan or re-cast.`
                          : `${t.name} — open the Talent Calendar`}
                        onClick={() => onOpenTalentCalendar && onOpenTalentCalendar()}
                        style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                          padding: '2px 6px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                          background: broken ? 'rgba(212,85,85,0.15)' : 'var(--bg-elevated)',
                          color: broken ? 'var(--s-incomplete-t)' : 'var(--tx-primary)',
                          border: broken ? '1px solid rgba(212,85,85,0.6)' : '1px solid var(--border)',
                        }}
                      >
                        {broken ? '⚠ ' : ''}{t.initials || t.name}
                      </span>
                    );
                  });
                  return chips.length ? chips : <span style={{ fontSize: 11, color: 'var(--tx-faint)' }}>—</span>;
                })()}
              </div>
              <div style={{ minWidth: 0 }}>
                {/* D-056: notes edit for any internal user — all readers here
                    are internal; the server refuses externals. */}
                {row.isSeries ? (
                  <span style={{ fontSize: 11, color: 'var(--tx-faint)' }}>Notes live on each episode</span>
                ) : notesEdit?.liveProductionId === row.liveProductionId ? (
                  <input
                    autoFocus
                    className="form-input"
                    style={{ fontSize: 11, padding: '4px 8px', width: '100%' }}
                    value={notesEdit.value}
                    onChange={e => setNotesEdit({ ...notesEdit, value: e.target.value })}
                    onBlur={saveNotes}
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveNotes();
                      if (e.key === 'Escape') setNotesEdit(null);
                    }}
                  />
                ) : (
                  <div
                    onClick={() => setNotesEdit({ liveProductionId: row.liveProductionId, value: row.notes || '' })}
                    title={row.isRiff
                      ? 'Riff cards keep their title here — editing this edits the card title'
                      : 'Click to edit notes (same field as the Production page’s Notes)'}
                    style={{
                      fontSize: 11, color: row.notes ? 'var(--tx-secondary)' : 'var(--tx-faint)',
                      cursor: 'text',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      padding: '4px 6px', border: '1px dashed transparent', borderRadius: 4,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    {row.isRiff && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--violet)', marginRight: 5, textTransform: 'uppercase' }}>Title</span>}
                    {row.notes || '+ add'}
                  </div>
                )}
              </div>
              {/* No action buttons (owner call 2026-09-02): the title opens
                  the card, and scheduling happens from the Talent Calendar.
                  Un-assigning = delete the segments in the card's Shoot
                  Scheduler (membership is derived, the row leaves itself). */}
              <div />
            </div>
    );
    if (!row.isSeries || !isSeriesExpanded(row)) return [main];
    return [main, ...row.episodes.map(ep => renderEpisodeChild(ep, row))];
  };

  return (
    <div className="cal-grid-wrap">
      {/* Sub-tabs + filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          {['ready', 'assigned'].map(k => (
            <button
              key={k}
              onClick={() => { setActiveTab(k); setPage(1); }}
              style={{
                padding: '6px 14px', fontSize: 12, cursor: 'pointer', border: 'none',
                background: activeTab === k ? 'var(--violet)' : 'transparent',
                color:      activeTab === k ? '#fff' : 'var(--tx-secondary)',
                fontWeight: activeTab === k ? 600 : 400, fontFamily: 'var(--font)',
              }}
            >
              {k === 'ready' ? 'Ready' : 'Assigned'}
            </button>
          ))}
        </div>

        <select
          className="form-input"
          style={{ fontSize: 12, padding: '5px 10px', width: 180 }}
          value={clientId || ''}
          onChange={e => pickClient(e.target.value)}
          title="Filter by project"
        >
          <option value="">All projects</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          {/* keep the current selection pickable even when this tab has no rows for it */}
          {clientId && !clients.some(c => c.id === clientId) && (
            <option value={clientId}>(current project — no rows in this tab)</option>
          )}
        </select>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 9, color: 'var(--tx-faint)', fontSize: 12, pointerEvents: 'none' }}>⌕</span>
          <input
            type="text"
            value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="Search titles…"
            style={{
              paddingLeft: 26, paddingRight: 10, paddingTop: 5, paddingBottom: 5,
              fontSize: 12, background: 'var(--bg-hover)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--tx-primary)', fontFamily: 'var(--font)',
              outline: 'none', width: 170,
            }}
          />
        </div>

        {activeTab === 'ready' && (
          <>
            <label style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 6 }}
                   title="Keep scripts whose resolved talent are all available on this date. Scripts with no talent assigned always stay listed.">
              Talent available on
              <input
                type="date"
                className="form-input"
                style={{ fontSize: 11, padding: '4px 8px', width: 140 }}
                value={availableOn}
                onChange={e => { setAvailableOn(e.target.value); setPage(1); }}
              />
              {availableOn && (
                <button className="btn-ghost" style={{ fontSize: 10, padding: '2px 6px' }}
                        onClick={() => { setAvailableOn(''); setPage(1); }}>clear</button>
              )}
            </label>
            <label style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
                   title="Include scripts hidden with ✕ so they can be recovered">
              <input type="checkbox" checked={showHidden} onChange={e => { setShowHidden(e.target.checked); setPage(1); }} />
              Show hidden
            </label>
          </>
        )}

        <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px', marginLeft: 'auto' }} onClick={load}>↺ Refresh</button>
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)', marginBottom: 10 }}>{error}</div>}

      {loading ? (
        <div className="cal-loading">Loading…</div>
      ) : tabRows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)' }}>
          {activeTab === 'ready'
            ? 'No scripts in Ready for this filter. Approved Skit and Mockumentary scripts appear here until their CAM segments are assigned — click a row to assign.'
            : 'No assigned scripts for this filter. Confirm a plan from the Ready tab and the script lands here.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Column headers — match the row gridTemplateColumns below. */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: activeTab === 'ready'
              ? '2.2fr 1.1fr 0.8fr 1fr 1.6fr auto'
              : '2fr 1fr 1.1fr 1.4fr 1.8fr auto',
            gap: 12, alignItems: 'center',
            padding: '4px 14px 6px',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--tx-muted)',
            borderBottom: '1px solid var(--border)',
          }}>
            <div>Script</div>
            <div>Project</div>
            {activeTab === 'ready' ? (
              <>
                <div>Format</div>
                <div>Qual Status</div>
                <div title="Resolved from confirmed segments, per-script overrides, then the project registry default">Talent</div>
              </>
            ) : (
              <>
                <div>Shoot Day(s)</div>
                <div title="Talent confirmed on the scheduled shoot segments">Talent</div>
                <div title="Producer notes — the Production page's Notes. On Riff productions this edits the title.">Notes</div>
              </>
            )}
            <div style={{ width: 150 }} />
          </div>

          {activeTab === 'ready' && tabRows.map(row => {
            const pill = QUAL_PILL[row.qualStatus] || {};
            return (
              <div
                key={row.scriptId}
                ref={focus?.scriptId === row.scriptId ? focusRef : undefined}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2.2fr 1.1fr 0.8fr 1fr 1.6fr auto',
                  gap: 12, alignItems: 'center',
                  padding: '10px 14px',
                  background: 'var(--bg-card)',
                  border: focus?.scriptId === row.scriptId ? '1px solid var(--violet)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  opacity: row.hidden ? 0.55 : 1,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  {/* 2026-09-02 overhaul: clicking an Approved script opens the
                      Schedule Plan modal to assign CAM segments — NOT the
                      script editor (that moved to the ✎ button). */}
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', cursor: canWrite ? 'pointer' : 'default', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    onClick={() => canWrite && setPlanFor(row.scriptId)}
                    title={canWrite
                      ? `${row.scriptName} — assign CAM segments and talent`
                      : row.scriptName}
                  >
                    {row.scriptName}
                    {row.hidden && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--tx-muted)', fontWeight: 400 }}>(hidden)</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 1 }}>
                    {row.writer?.name && `Writer: ${row.writer.name}`}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>{row.client?.name || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{row.format}</div>
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                    background: pill.bg || 'var(--bg-elevated)', color: pill.fg || 'var(--tx-secondary)',
                  }}>
                    {row.qualStatus}
                  </span>
                </div>
                <TalentChips row={row} />
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                  {canWrite && (
                    <button
                      className="btn-primary"
                      style={{ fontSize: 11, padding: '5px 12px' }}
                      onClick={() => setPlanFor(row.scriptId)}
                      title="Assign CAM segments and talent — the script moves to Assigned"
                    >
                      Assign →
                    </button>
                  )}
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 11, padding: '5px 8px' }}
                    onClick={() => openInEditor(row.scriptId)}
                    title="Open in the script editor"
                  >
                    ✎
                  </button>
                  {canWrite && !row.hidden && (
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: '5px 8px' }}
                      onClick={() => handleHide(row.scriptId)}
                      title="Hide from the Ready tab (recover with Show hidden)"
                    >
                      ✕
                    </button>
                  )}
                  {canWrite && row.hidden && (
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: '5px 10px' }}
                      onClick={() => handleUnhide(row.scriptId)}
                      title="Return this script to the Ready tab"
                    >
                      Unhide
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {activeTab === 'assigned' && tabRows.flatMap(row => renderAssignedRows(row))}
        </div>
      )}

      {/* Pagination */}
      {!loading && data.total > (data.pageSize || 50) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
          <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }}
                  disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>
            Page {page} of {totalPages} · {data.total} script{data.total !== 1 ? 's' : ''}
          </span>
          <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }}
                  disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}

      {planFor && (
        <SchedulePlanModal
          scriptId={planFor}
          onClose={() => setPlanFor(null)}
          onConfirmed={() => {
            setPlanFor(null);
            load();
            // The new segments must reach the Shoot Schedule tab's records too.
            if (onScheduled) onScheduled();
          }}
        />
      )}
    </div>
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const showTalentPool = canSeeTalentAvailability(user);
  const assignTalent   = canAssignTalent(user);

  // Return trip from the script editor: opening a script from the Script List
  // hands /scripts a backTo ticket, and its back button navigates here with
  // { calendarInit } so the user lands on the exact tab they left.
  const calendarInit = location.state?.calendarInit || null;

  const [pageTab,         setPageTab]         = useState(calendarInit?.pageTab || 'assign');
  const [display,         setDisplay]         = useState(() => localStorage.getItem('cal_view') || 'month');
  const [shootRecords,    setShootRecords]    = useState([]);
  const [members,         setMembers]         = useState([]);
  const [allTalent,       setAllTalent]       = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [selectedDay,     setSelectedDay]     = useState(null);
  const [talentForDay,    setTalentForDay]    = useState([]);
  const [talentLoading,   setTalentLoading]   = useState(false);
  const [talentCounts,    setTalentCounts]    = useState({}); // { "YYYY-MM-DD": count }
  const talentCountsKey   = useRef(null); // tracks the last range fetched
  const [dayDetailDate,   setDayDetailDate]   = useState(null);
  const [schedulerProject, setSchedulerProject] = useState(null);
  // Production page → "🎬 Shoot Scheduler" (P-3 Shoot stage): open the panel for
  // that record once the shoot records are loaded, then consume the state.
  useEffect(() => {
    const id = location.state?.openSchedulerFor;
    if (!id || !shootRecords?.length) return;
    const rec = shootRecords.find(r => r.id === id);
    if (rec) setSchedulerProject(rec);
    navigate('/calendar', { replace: true, state: {} });
  }, [location.state, shootRecords]); // eslint-disable-line react-hooks/exhaustive-deps
  // Staff editing availability on a talent's behalf: { id, name } | null
  const [availabilityTalent, setAvailabilityTalent] = useState(null);
  // Cross-tab jump into the Script List (peek card "Detail", §6.5):
  // { tab, scriptId?, liveProductionId? } | null
  // D-088: the Production page's 🎬 Shoot Scheduler hands over
  // { calendarInit, scriptListFocus } so the list view opens on that
  // production's row (Ready or Assigned). The ticket is consumed on mount.
  const [scriptListFocus, setScriptListFocus] = useState(location.state?.scriptListFocus || null);
  useEffect(() => {
    if (location.state?.scriptListFocus) navigate('/calendar', { replace: true, state: {} });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem('cal_view', display); }, [display]);

  const loadShoot = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/live-production/shoot-calendar');
      setShootRecords(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadShoot(); }, [loadShoot]);
  // Scheduling happens on the Script List tab (segments created server-side,
  // not in this page's state), so re-pull the shoot records every time the
  // Shoot Schedule tab is opened — a fresh booking must show without a page
  // reload (Kendra's missing chip, 2026-09-01).
  useEffect(() => { if (pageTab === 'shoot') loadShoot(); }, [pageTab, loadShoot]);
  useEffect(() => {
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!showTalentPool) return;
    api.get('/api/talent').then(r => setAllTalent(r.data)).catch(() => {});
  }, [showTalentPool]);

  // Load talent availability counts for a date range (for indicators)
  const loadTalentCounts = useCallback(async (from, to) => {
    if (!showTalentPool) return;
    const key = `${from}|${to}`;
    if (talentCountsKey.current === key) return;
    talentCountsKey.current = key;
    try {
      const res = await api.get(`/api/talent-availability/date-counts?from=${from}&to=${to}`);
      setTalentCounts(res.data);
    } catch { /* silent */ }
  }, [showTalentPool]);

  // Load talent available for a specific selected day
  const loadTalentForDay = useCallback(async (dateStr) => {
    if (!showTalentPool || !dateStr) return;
    setTalentLoading(true);
    setTalentForDay([]);
    try {
      const res = await api.get(`/api/talent-availability/by-date?date=${dateStr}`);
      setTalentForDay(res.data);
    } catch { /* silent */ } finally { setTalentLoading(false); }
  }, [showTalentPool]);

  // When a day is selected (from chip pool context) load talent
  useEffect(() => {
    if (selectedDay) loadTalentForDay(selectedDay);
    else setTalentForDay([]);
  }, [selectedDay, loadTalentForDay]);

  // Drag a chip from one day to another → PATCH that specific segment's shoot_day.
  const handleMoveSegment = useCallback(async (segmentId, dateStr) => {
    setShootRecords(prev => prev.map(r => ({
      ...r,
      shootSegments: (r.shootSegments || []).map(s => s.id === segmentId ? { ...s, shootDay: dateStr } : s),
    })));
    try {
      await api.patch(`/api/shoot-segments/${segmentId}`, { shootDay: dateStr });
    } catch {
      loadShoot();
      alert('Failed to move segment.');
    }
  }, [loadShoot]);

  // Chip × or peek "Remove Date" → delete the segment entirely. Keeps the
  // calendar and the Scheduler modal in lockstep — removing the chip on the
  // calendar removes the same Cam from the Scheduler. Flipped from "null out
  // shoot_day" on 2026-05-16: leaving empty segments behind felt like clutter.
  const handleUnscheduleSegment = useCallback(async (segmentId) => {
    setShootRecords(prev => prev.map(r => ({
      ...r,
      shootSegments: (r.shootSegments || []).filter(s => s.id !== segmentId),
    })));
    try {
      await api.delete(`/api/shoot-segments/${segmentId}`);
    } catch {
      loadShoot();
      alert('Failed to remove segment.');
    }
  }, [loadShoot]);

  // Assign talent to a specific live production record
  const handleAssignTalent = useCallback(async (liveId, talentChip) => {
    if (!assignTalent) return;
    try {
      const res = await api.patch(`/api/live-production/${liveId}`, { talentId: talentChip.talentId });
      setShootRecords(prev => prev.map(r => r.id === liveId ? { ...r, ...res.data } : r));
    } catch {
      alert('Failed to assign talent.');
    }
  }, [assignTalent]);

  const handleUpdateTimeSlot = useCallback(async (recId, timeSlot) => {
    setShootRecords(prev => prev.map(r => r.id === recId ? { ...r, shootTimeSlot: timeSlot } : r));
    try {
      await api.patch(`/api/live-production/${recId}`, { shootTimeSlot: timeSlot });
    } catch {
      loadShoot();
      alert('Failed to update time slot.');
    }
  }, [loadShoot]);

  const handleBulkReorder = useCallback(async (date, order) => {
    const orderMap = Object.fromEntries(order.map(({ id, shootOrder }) => [id, shootOrder]));
    setShootRecords(prev => prev.map(r => orderMap[r.id] !== undefined ? { ...r, shootOrder: orderMap[r.id] } : r));
    try {
      await api.post('/api/live-production/reorder', { order });
    } catch {
      loadShoot();
      alert('Failed to reorder shoots.');
    }
  }, [loadShoot]);

  // Segment-level time slot: each Cam carries its own shoot_time_slot so two
  // Cams of the same project on different days don't inherit one shared hour.
  const handleSegmentTimeSlot = useCallback(async (segmentId, timeSlot) => {
    setShootRecords(prev => prev.map(r => ({
      ...r,
      shootSegments: (r.shootSegments || []).map(s => s.id === segmentId ? { ...s, shootTimeSlot: timeSlot } : s),
    })));
    try {
      await api.patch(`/api/shoot-segments/${segmentId}`, { shootTimeSlot: timeSlot });
    } catch {
      loadShoot();
      alert('Failed to update segment time slot.');
    }
  }, [loadShoot]);

  // Segment-level reorder within a single day (POST /api/shoot-segments/reorder).
  const handleSegmentReorder = useCallback(async (date, order) => {
    const orderMap = Object.fromEntries(order.map(({ id, sortOrder }) => [id, sortOrder]));
    setShootRecords(prev => prev.map(r => ({
      ...r,
      shootSegments: (r.shootSegments || []).map(s => orderMap[s.id] !== undefined ? { ...s, sortOrder: orderMap[s.id] } : s),
    })));
    try {
      await api.post('/api/shoot-segments/reorder', { order });
    } catch {
      loadShoot();
      alert('Failed to reorder segments.');
    }
  }, [loadShoot]);

  const openDayDetail = useCallback((key) => {
    setDayDetailDate(key);
    setSelectedDay(key);
  }, []);

  // A project is "unscheduled" if it's in an active status AND (a) has no
  // segments yet (user must create them in the Scheduler first), OR (b) has at
  // least one segment whose shoot_day is null (still needs placement).
  // The drag pool stays Approved-only (D-053): Needs-Review scripts schedule
  // only through the deliberate day modal, never by drag. Riff cards carry no
  // script and stay draggable as before.
  const unscheduledRecords = useMemo(
    () => shootRecords.filter(r => {
      if (!['Scripted', 'Filming', 'Incomplete'].includes(r.status)) return false;
      if (r.script && r.script.qualStatus !== 'Approved') return false;
      const segs = r.shootSegments || [];
      if (segs.length === 0) return true;
      return segs.some(s => !s.shootDay);
    }),
    [shootRecords]
  );

  // Script List "Open →": fetch the full record and open the Video Card modal
  // right here — never bounce through the Live Production list view.
  // Script List → Assigned title. The Video Card MODAL retired with the
  // Production page (2026-09-13) but this handler (and the Day Detail modal's
  // ↗) still fed the now-unrendered videoCard state — the click did nothing
  // (DEF-103, found 2026-09-15 by the D-088 hand-off test). Open the page.
  const handleOpenCardById = useCallback((liveProductionId) => {
    navigate(`/production/${liveProductionId}`);
  }, [navigate]);


  // Records scheduled on selectedDay (for talent drag target) — segment-based.
  const recordsOnSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    return shootRecords.filter(r =>
      (r.shootSegments || []).some(s => s.shootDay?.slice(0, 10) === selectedDay)
    );
  }, [shootRecords, selectedDay]);

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Production Calendar</div>
          <div className="page-sub">
            {pageTab === 'shoot'
              ? `Shoot schedule · ${unscheduledRecords.length} script${unscheduledRecords.length !== 1 ? 's' : ''} awaiting shoot date`
              : pageTab === 'talent'
                ? 'Talent availability overview'
                : 'Scripts ready to schedule, and what is already on the calendar'}
          </div>
        </div>
        <div className="topbar-actions">
          {pageTab === 'shoot' && (
            <button className="btn-ghost" onClick={loadShoot}>↺ Refresh</button>
          )}
          {pageTab === 'shoot' && (
            <div style={{ display: 'flex', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              {VIEWS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setDisplay(key)}
                  style={{
                    padding: '7px 16px', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer', border: 'none',
                    background: display === key ? 'var(--violet)' : 'transparent',
                    color: display === key ? '#fff' : 'var(--tx-secondary)',
                    fontWeight: display === key ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Page tab bar — staff only. The Script List is readable by the whole
          tab-bar set (§5.1); write actions stay Producer/Power User/Admin and
          are enforced server-side, not by hiding this tab. */}
      {showTalentPool && (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 36px', flexShrink: 0, background: 'var(--bg-app)' }}>
          {PAGE_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setPageTab(t.key)}
              style={{
                padding: '10px 20px', fontSize: 13, fontFamily: 'var(--font)', cursor: 'pointer',
                border: 'none', borderBottom: pageTab === t.key ? '2px solid var(--violet)' : '2px solid transparent',
                background: 'transparent', color: pageTab === t.key ? 'var(--violet)' : 'var(--tx-secondary)',
                fontWeight: pageTab === t.key ? 600 : 400, marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Scrollable content */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 36px 32px' }}>
        {/* Talent Calendar tab */}
        {pageTab === 'talent' && <TalentCalendarView />}
        {pageTab === 'assign' && (
          <ScriptListView
            focus={scriptListFocus}
            onFocusConsumed={() => setScriptListFocus(null)}
            initialTab={calendarInit?.scriptListTab}
            onOpenCard={handleOpenCardById}
            onScheduled={loadShoot}
            onOpenShootDay={d => { setPageTab('shoot'); setDayDetailDate(d); setSelectedDay(d); }}
            onOpenTalentCalendar={() => setPageTab('talent')}
          />
        )}

        {/* Shoot Schedule tab */}
        {pageTab === 'shoot' && loading && <div className="cal-loading">Loading…</div>}

        {pageTab === 'shoot' && !loading && display === 'month' && (
          <CalendarGrid
            records={shootRecords}
            talentCounts={showTalentPool ? talentCounts : {}}
            onMoveSegment={handleMoveSegment}
            onUnscheduleSegment={handleUnscheduleSegment}
            onOpenVideoCard={rec => navigate(`/production/${rec.id}`)}
            onLoadCounts={loadTalentCounts}
            onDaySelect={setSelectedDay}
            onAssignTalent={assignTalent ? handleAssignTalent : null}
            onDayDetailOpen={openDayDetail}
          />
        )}

        {pageTab === 'shoot' && !loading && display === 'week' && (
          <WeekGrid
            records={shootRecords}
            onMoveSegment={handleMoveSegment}
            onUnscheduleSegment={handleUnscheduleSegment}
            onOpenVideoCard={rec => navigate(`/production/${rec.id}`)}
          />
        )}

        {pageTab === 'shoot' && !loading && display === 'day' && (
          <DayScheduleView
            shootRecords={shootRecords}
            onOpenVideoCard={rec => navigate(`/production/${rec.id}`)}
            onUpdateTimeSlot={handleUpdateTimeSlot}
            onReorder={handleBulkReorder}
            unscheduled={unscheduledRecords}
          />
        )}

        {/* Talent pool — shoot schedule tab only. The Approved-Scripts drag
            pool is GONE (owner call 2026-09-01): scheduling lives in the
            Script List and the Talent Calendar's booking flow; this view is
            read-and-edit. */}
        {pageTab === 'shoot' && !loading && showTalentPool && (
          <div style={{ marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <div className="eq-unscheduled-label" style={{ marginBottom: 10 }}>Talent</div>
            {showTalentPool && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginBottom: 10 }}>
                  {!selectedDay
                    ? 'Select a date on the calendar to see availability'
                    : talentLoading
                      ? 'Loading availability…'
                      : `${selectedDay} · ${talentForDay.length} of ${allTalent.length} available`
                  }
                  {selectedDay && !talentLoading && assignTalent && recordsOnSelectedDay.length > 0 && ' · drag green chips to assign'}
                </div>
                {allTalent.length === 0 ? (
                  <div style={{ color: 'var(--tx-faint)', fontSize: 12 }}>No talent accounts yet.</div>
                ) : (
                  <div className="eq-unscheduled-list">
                    {allTalent.map(t => {
                      const noColor  = !selectedDay || talentLoading;
                      const avEntry  = noColor ? null : talentForDay.find(av => av.talentId === t.id);
                      const isAvail  = Boolean(avEntry);
                      const draggable = isAvail; // assignTalent guards the drop, not the drag

                      const accentColor = noColor ? 'var(--border)'
                        : isAvail ? 'rgba(74,222,128,0.6)' : 'rgba(212,85,85,0.45)';
                      const chipBg = noColor ? 'transparent'
                        : isAvail ? 'rgba(74,222,128,0.07)' : 'rgba(212,85,85,0.06)';
                      const textColor = noColor ? 'var(--tx-secondary)'
                        : isAvail ? '#4ade80' : 'var(--s-incomplete-t)';

                      const label = isAvail
                        ? (avEntry.availabilityType === 'full_day'
                            ? `${t.name} — All day`
                            : `${t.name} — ${fmtTime12(avEntry.blockStart)} to ${fmtTime12(avEntry.blockEnd)}`)
                        : t.name;

                      const canEdit = assignTalent; // Admin / Power User / Producer
                      const cursor = canEdit ? (draggable ? 'grab' : 'pointer') : (draggable ? 'grab' : 'default');
                      return (
                        <div
                          key={t.id}
                          className="eq-chip eq-chip-flat"
                          draggable={draggable}
                          onDragStart={draggable ? (e => {
                            e.stopPropagation();
                            e.dataTransfer.setData('talent-chip', JSON.stringify({ ...avEntry, talentId: t.id, talentName: t.name }));
                          }) : undefined}
                          onDragEnd={draggable ? () => {} : undefined}
                          onClick={canEdit ? (() => setAvailabilityTalent({ id: t.id, name: t.name })) : undefined}
                          title={canEdit ? 'Click to edit availability for this talent' : undefined}
                          style={{ borderLeftColor: accentColor, background: chipBg, cursor }}
                        >
                          <span className="eq-chip-title" style={{ color: textColor }}>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>


      {schedulerProject && (
        <SchedulerPanel
          record={schedulerProject}
          allTalent={allTalent}
          // Refresh on close so segments added/edited in the modal (which
          // SchedulerPanel manages in its own local state) immediately show up
          // as chips on the calendar behind it.
          onClose={() => { setSchedulerProject(null); loadShoot(); }}
          onProjectUpdated={updated => {
            setShootRecords(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
            setSchedulerProject(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
          }}
        />
      )}

      {dayDetailDate && (
        <DayDetailModal
          dateStr={dayDetailDate}
          entries={shootRecords.flatMap(rec =>
            (rec.shootSegments || [])
              .filter(s => s.shootDay?.slice(0, 10) === dayDetailDate)
              .map(segment => ({ segment, rec }))
          )}
          talentForDay={talentForDay}
          canReorder={canReorderShoots(user)}
          onClose={() => setDayDetailDate(null)}
          onSegmentTimeSlot={handleSegmentTimeSlot}
          onSegmentReorder={handleSegmentReorder}
          onOpenVideoCard={r => { setDayDetailDate(null); navigate(`/production/${r.id}`); }}
        />
      )}

      {availabilityTalent && (
        <div
          className="modal-overlay"
          onClick={() => {
            setAvailabilityTalent(null);
            // Refresh availability for the currently selected day so the chip
            // reflects any edits made through this modal.
            if (selectedDay) loadTalentForDay(selectedDay);
            talentCountsKey.current = null; // force date-counts refresh next render
          }}
        >
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 760, maxWidth: '95vw' }}>
            <div className="modal-header">
              <div className="modal-title">Availability — {availabilityTalent.name}</div>
              <button
                className="vc-close"
                onClick={() => {
                  setAvailabilityTalent(null);
                  if (selectedDay) loadTalentForDay(selectedDay);
                  talentCountsKey.current = null;
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 12 }}>
                Click any date to mark or edit availability on behalf of this talent.
              </div>
              <TalentAvailabilityView talentId={availabilityTalent.id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
